// Tool: Upsell & Cross-sell — bannery a nabídky na e-shopu.

import { Toast } from '@/components/Toast';
import { OfferObdobi, denToMs, msToDen } from '@/components/upsell/OfferObdobi';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime } from '@/lib/format';
import { hasModule } from '@/lib/modules';
import type { MeResponse } from '@sklad-sync/shared';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';

type Props = { user: MeResponse };

type Tab = 'banners' | 'offers';
type BannersView = 'list' | 'new' | 'settings';

export type BannerPlacement =
  | 'product-photos'
  | 'category-top'
  | 'category-bottom'
  | 'category-products'
  | 'homepage-main'
  | 'ear-left'
  | 'ear-right';

export type OfferPlacement =
  | 'offer-product-bottom'
  | 'offer-above-add-to-cart'
  | 'offer-cart'
  | 'offer-checkbox';

// Sjednocený typ pro Nastavení umístění (selektory) — zahrnuje obě skupiny.
type AnyPlacement = BannerPlacement | OfferPlacement;

const PLACEMENTS: Array<{
  id: BannerPlacement;
  label: string;
  image: string;
  description: string;
}> = [
  {
    id: 'product-photos',
    label: 'Na detailu produktu u fotek',
    image: '/assets/banner-placements/product-photos.png',
    description: 'Zobrazí se vedle fotogalerie na detailu produktu.',
  },
  {
    id: 'category-top',
    label: 'Na stránce kategorie nahoře',
    image: '/assets/banner-placements/category-top.png',
    description: 'Nad výpisem produktů v kategorii.',
  },
  {
    id: 'category-bottom',
    label: 'Na stránce kategorie dole',
    image: '/assets/banner-placements/category-bottom.png',
    description: 'Pod výpisem produktů v kategorii.',
  },
  {
    id: 'category-products',
    label: 'Na místě produktu v kategorii',
    image: '/assets/banner-placements/category-products.png',
    description: 'Nahradí jednu dlaždici produktu uvnitř mřížky kategorie.',
  },
  {
    id: 'homepage-main',
    label: 'Úvodní stránka — pravý banner',
    image: '/assets/banner-placements/homepage-main.svg',
    description:
      'Pravý z dvojice horních bannerů na úvodní stránce. Podporuje typ „Produkt týdne" (obrázek + ceny + odpočet do konce akce).',
  },
  {
    id: 'ear-left',
    label: 'Levé ucho stránky',
    image: '/assets/banner-placements/ear-left.svg',
    description:
      'Vertikální banner přilepený k levé straně viewportu (mimo obsah). Skrytý pod 1800 px.',
  },
  {
    id: 'ear-right',
    label: 'Pravé ucho stránky',
    image: '/assets/banner-placements/ear-right.svg',
    description:
      'Vertikální banner přilepený k pravé straně viewportu (mimo obsah). Skrytý pod 1800 px.',
  },
];

const OFFER_PLACEMENTS: Array<{
  id: OfferPlacement;
  label: string;
  image: string;
  description: string;
}> = [
  {
    id: 'offer-product-bottom',
    label: 'Pod detailem produktu',
    image: '/assets/banner-placements/offer-product-bottom.svg',
    description: 'Sekce „Související produkty" pod popisem produktu — grid 1–6 doporučených.',
  },
  {
    id: 'offer-above-add-to-cart',
    label: 'Nad košíkem na detailu produktu',
    image: '/assets/banner-placements/offer-above-add-to-cart.svg',
    description:
      'Cross-sell box těsně nad tlačítkem „Přidat do košíku" — vyšší šance, že si zákazník přidá doplněk.',
  },
  {
    id: 'offer-cart',
    label: 'V košíku',
    image: '/assets/banner-placements/offer-cart.svg',
    description: 'Cross-sell v košíku — „Mohlo by se vám hodit" pod položkami objednávky.',
  },
  {
    id: 'offer-checkbox',
    label: 'Zaškrtávátko nad „Do košíku"',
    image: '/assets/banner-placements/offer-checkbox.svg',
    description:
      'Jeden doplněk k zaškrtnutí (např. vánoční balení). Přidá se s produktem, do košíku nejvýš 1×.',
  },
];

function isOfferPlacement(p: AnyPlacement): p is OfferPlacement {
  return (
    p === 'offer-product-bottom' ||
    p === 'offer-cart' ||
    p === 'offer-above-add-to-cart' ||
    p === 'offer-checkbox'
  );
}

function labelForAnyPlacement(p: AnyPlacement): string {
  return (
    PLACEMENTS.find((x) => x.id === p)?.label ??
    OFFER_PLACEMENTS.find((x) => x.id === p)?.label ??
    p
  );
}

type PlacementSettings = Record<string, { css_selector: string | null; updated_at: number }>;

type CatalogRun = {
  status: 'success' | 'partial' | 'failed';
  run_at: number;
  items_fetched: number;
  items_upserted: number;
  error_message: string | null;
} | null;

type CatalogStatus = {
  categories: { count: number; last_run: CatalogRun };
  flags: { count: number; last_run: CatalogRun };
  stocks?: { count: number; last_run: CatalogRun };
};

type SyncKind = 'categories' | 'flags' | 'stocks';

// ─── Trigger (step 2) typy ──────────────────────────────────────────────

export type TriggerType = 'products' | 'category' | 'flag' | 'everywhere';

type ProductLite = {
  shopitem_id: number;
  name: string;
  parent_code: string | null;
  image_url: string | null;
};

type CategoryRow = {
  guid: string;
  parent_guid: string | null;
  name: string;
  full_name: string | null;
  visible: number;
  sort_order: number;
};

type FlagRow = { code: string; title: string; active: number };

type UploadedImage = {
  // Pro nový upload: 'data:image/...;base64,...' (BE dekóduje a uloží do R2).
  // Pro již existující grafiku (edit mode): R2 URL (BE pozná, že keep=true).
  dataUrl: string;
  fileName: string;
  size: number | null; // null = neznáme (existující bez meta nebo svg)
  mimeType: string;
  width: number | null;
  height: number | null;
  isExisting?: boolean; // true = nezměněná grafika načtená z editovaného banneru
};

type BannerDto = {
  id: number;
  placement: BannerPlacement;
  trigger_type: TriggerType;
  trigger_product_ids: number[];
  trigger_category_guids: string[];
  trigger_flag_codes: string[];
  image_desktop_url: string | null;
  image_mobile_url: string | null;
  target_url: string | null;
  target_blank: boolean;
  alt_text: string | null;
  internal_name: string;
  priority: number;
  start_at: number | null;
  end_at: number | null;
  grid_position: number | null;
  banner_type: 'image' | 'review' | 'produkt_tydne';
  review_rating: number | null;
  review_title: string | null;
  review_body: string | null;
  review_author: string | null;
  pt_price_original: string | null;
  pt_price_new: string | null;
  pt_discount: string | null;
  active: boolean;
  created_at: number;
  impressions: number;
  clicks: number;
};

type OfferDto = {
  id: number;
  internal_name: string;
  placement: OfferPlacement;
  trigger_type: TriggerType;
  trigger_product_ids: number[];
  trigger_category_guids: string[];
  trigger_flag_codes: string[];
  exclude_product_ids: number[]; // produkty, kde se nabídka nezobrazí
  product_ids: number[]; // doporučené produkty (1–6) v pořadí
  section_title: string | null;
  priority: number;
  start_at: number | null;
  end_at: number | null;
  active: boolean;
  created_at: number;
  impressions: number;
  clicks: number;
  cart_clicks: number;
  trigger_product: {
    shopitem_id: number;
    name: string;
    image_url: string | null;
    parent_code: string | null;
  } | null;
  recommended_products: {
    shopitem_id: number;
    name: string;
    image_url: string | null;
    parent_code: string | null;
    product_clicks: number;
    cart_clicks: number;
  }[];
  preview_product: {
    shopitem_id: number;
    name: string;
    image_url: string | null;
    parent_code: string | null;
  } | null;
};

// V košíku nemáme kontext konkrétního produktu — povolíme jen kategorie a všude.
function allowedTriggersForOffer(placement: OfferPlacement): TriggerType[] {
  if (
    placement === 'offer-product-bottom' ||
    placement === 'offer-above-add-to-cart' ||
    placement === 'offer-checkbox'
  ) {
    return ['products', 'category', 'flag', 'everywhere'];
  }
  return ['category', 'everywhere'];
}

const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3 MB
const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml',
];

/**
 * Pro kategoriové placementy nemá smysl trigger „konkrétní produkt"
 * ani „příznak" — na kategoriové stránce není kontext produktu.
 * Uši (ear-left/ear-right) mohou viset na produktu i kategorii — povolíme vše.
 */
function allowedTriggersFor(placement: BannerPlacement): TriggerType[] {
  if (placement === 'product-photos' || isEarPlacement(placement)) {
    return ['products', 'category', 'flag', 'everywhere'];
  }
  // Úvodní stránka nemá kontext produktu ani kategorie → jen „všude".
  if (placement === 'homepage-main') {
    return ['everywhere'];
  }
  return ['category', 'everywhere'];
}

function isEarPlacement(placement: AnyPlacement): boolean {
  return placement === 'ear-left' || placement === 'ear-right';
}

const TRIGGER_TYPES: Array<{ id: TriggerType; label: string; description: string }> = [
  {
    id: 'products',
    label: 'Konkrétní produkty',
    description: 'Banner se zobrazí jen na detailu/výpisu vybraných produktů.',
  },
  {
    id: 'category',
    label: 'Produkt z konkrétní kategorie',
    description: 'Banner se zobrazí na produktech z vybraných kategorií e-shopu.',
  },
  {
    id: 'flag',
    label: 'Produkt s konkrétním příznakem',
    description: 'Banner se zobrazí jen u produktů označených zvoleným příznakem (Akce, Novinka…).',
  },
  {
    id: 'everywhere',
    label: 'Všude',
    description: 'Banner se zobrazí kdekoli, kde dané umístění existuje.',
  },
];

export function UpsellCrosssellPage({ user }: Props) {
  // Edit/delete/create akce povolujeme každému s modulem `upsell` — route je
  // už module-gated v App.tsx, takže tady to vždy bude true. Necháváme proměnnou
  // `isAdmin` ze zpětné kompatibility (používá se v mnoha sub-komponentách).
  const isAdmin = hasModule(user.modules, 'upsell');
  const [tab, setTab] = useState<Tab>('banners');
  const [view, setView] = useState<BannersView>('list');
  const [draftPlacement, setDraftPlacement] = useState<BannerPlacement | null>(null);
  const [editingBanner, setEditingBanner] = useState<BannerDto | null>(null);
  const [settings, setSettings] = useState<PlacementSettings>({});
  const [catalogStatus, setCatalogStatus] = useState<CatalogStatus | null>(null);
  const [editingSelectorFor, setEditingSelectorFor] = useState<AnyPlacement | null>(null);
  const [banners, setBanners] = useState<BannerDto[]>([]);
  const [showingStocks, setShowingStocks] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  // ── Nabídky (cross-sell)
  const [offersView, setOffersView] = useState<'list' | 'new'>('list');
  const [offers, setOffers] = useState<OfferDto[]>([]);
  const [editingOffer, setEditingOffer] = useState<OfferDto | null>(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [combinationFor, setCombinationFor] = useState<OfferDto | null>(null);

  const loadSettings = useCallback(async () => {
    try {
      const data = await apiFetch<{ placements: PlacementSettings }>('/api/upsell/placements');
      setSettings(data.placements ?? {});
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.message : 'Nepodařilo se načíst nastavení',
        kind: 'error',
      });
    }
  }, []);

  const loadCatalogStatus = useCallback(async () => {
    try {
      const data = await apiFetch<CatalogStatus>('/api/upsell/catalog-status');
      setCatalogStatus(data);
    } catch {
      /* netěží — UI zvládne null */
    }
  }, []);

  const loadBanners = useCallback(async () => {
    try {
      const data = await apiFetch<{ banners: BannerDto[] }>('/api/upsell/banners');
      setBanners(data.banners ?? []);
    } catch {
      /* prázdné UI to zvládne */
    }
  }, []);

  const loadOffers = useCallback(async () => {
    try {
      const data = await apiFetch<{ offers: OfferDto[] }>('/api/upsell/offers');
      setOffers(data.offers ?? []);
    } catch {
      /* prázdné UI to zvládne */
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadCatalogStatus();
    loadBanners();
    loadOffers();
  }, [loadSettings, loadCatalogStatus, loadBanners, loadOffers]);

  async function toggleOfferActive(id: number, active: boolean) {
    setOffers((prev) => prev.map((o) => (o.id === id ? { ...o, active } : o)));
    try {
      await apiFetch(`/api/upsell/offers/${id}/active`, {
        method: 'PATCH',
        body: { active },
      });
      setToast({
        msg: active ? 'Nabídka aktivována' : 'Nabídka deaktivována',
        kind: 'success',
      });
    } catch (e: unknown) {
      setOffers((prev) => prev.map((o) => (o.id === id ? { ...o, active: !active } : o)));
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Změna stavu selhala',
        kind: 'error',
      });
    }
  }

  async function deleteOffer(id: number, name: string) {
    if (!confirm(`Smazat nabídku „${name}"?`)) return;
    try {
      await apiFetch(`/api/upsell/offers/${id}`, { method: 'DELETE' });
      setToast({ msg: 'Nabídka smazána', kind: 'success' });
      await loadOffers();
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Smazání selhalo',
        kind: 'error',
      });
    }
  }

  function startNewOffer() {
    setEditingOffer(null);
    setOffersView('new');
  }
  function startEditOffer(o: OfferDto) {
    setEditingOffer(o);
    setOffersView('new');
  }

  async function toggleBannerActive(id: number, active: boolean) {
    // Optimistic update — overrideneme po API odpovědi.
    setBanners((prev) => prev.map((b) => (b.id === id ? { ...b, active } : b)));
    try {
      await apiFetch(`/api/upsell/banners/${id}/active`, {
        method: 'PATCH',
        body: { active },
      });
      setToast({
        msg: active ? 'Banner aktivován' : 'Banner deaktivován',
        kind: 'success',
      });
    } catch (e: unknown) {
      // Revert optimistic při chybě
      setBanners((prev) => prev.map((b) => (b.id === id ? { ...b, active: !active } : b)));
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Změna stavu selhala',
        kind: 'error',
      });
    }
  }

  async function deleteBanner(id: number, name: string) {
    if (!confirm(`Smazat banner „${name}"?`)) return;
    try {
      await apiFetch(`/api/upsell/banners/${id}`, { method: 'DELETE' });
      setToast({ msg: 'Banner smazán', kind: 'success' });
      await loadBanners();
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Smazání selhalo',
        kind: 'error',
      });
    }
  }

  async function syncCatalog(kind: SyncKind) {
    try {
      const data = await apiFetch<{
        status: 'success' | 'partial' | 'failed';
        items_upserted: number;
        error_message: string | null;
      }>(`/api/upsell/sync/${kind}`, { method: 'POST' });
      const labels: Record<SyncKind, string> = {
        categories: 'Kategorie',
        flags: 'Příznaky',
        stocks: 'Skladová dostupnost',
      };
      if (data.status === 'success') {
        setToast({
          msg: `${labels[kind]}: ${data.items_upserted} ks synchronizováno`,
          kind: 'success',
        });
      } else {
        setToast({
          msg: data.error_message ?? 'Sync selhal',
          kind: 'error',
        });
      }
      await loadCatalogStatus();
    } catch (e: unknown) {
      // Backend pro selhání vrací 500 s tělem { error_message, ... };
      // ApiError ho má v details — vytáhneme ho, aby uživatel viděl konkrétní hlášku.
      let msg = 'Sync selhal';
      if (e instanceof ApiError) {
        const details = e.details as { error_message?: string } | undefined | null;
        if (details?.error_message) msg = details.error_message;
        else msg = e.describe();
      }
      setToast({ msg, kind: 'error' });
    }
  }

  function startNew() {
    setDraftPlacement(null);
    setEditingBanner(null);
    setView('new');
  }

  function startEdit(b: BannerDto) {
    setEditingBanner(b);
    setDraftPlacement(b.placement);
    setView('new');
  }

  async function saveSelector(placement: AnyPlacement, selector: string | null) {
    await apiFetch(`/api/upsell/placements/${placement}`, {
      method: 'PUT',
      body: { css_selector: selector },
    });
    setSettings((prev) => ({
      ...prev,
      [placement]: { css_selector: selector, updated_at: Date.now() },
    }));
    setToast({ msg: 'Umístění uloženo', kind: 'success' });
  }

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Upsell &amp; Cross-sell</h1>
        <p className="text-sm text-text-secondary mt-0.5">
          Bannery a nabídky vykreslované na e-shopu pro zvýšení hodnoty objednávky.
        </p>
      </div>

      <div className="border-b border-border mb-6">
        <nav className="flex gap-1 -mb-px">
          <TabButton active={tab === 'banners'} onClick={() => setTab('banners')}>
            Bannery
          </TabButton>
          <TabButton active={tab === 'offers'} onClick={() => setTab('offers')}>
            Nabídky
          </TabButton>
        </nav>
      </div>

      {tab === 'banners' && (
        <>
          {view === 'list' && (
            <BannersList
              banners={banners}
              isAdmin={isAdmin}
              onNew={startNew}
              onEdit={startEdit}
              onDelete={deleteBanner}
              onToggleActive={toggleBannerActive}
              onOpenSettings={isAdmin ? () => setView('settings') : null}
            />
          )}
          {view === 'new' && (
            <NewBannerWizard
              key={editingBanner ? `edit-${editingBanner.id}` : 'new'}
              placement={draftPlacement}
              onPickPlacement={setDraftPlacement}
              settings={settings}
              isAdmin={isAdmin}
              existingBanner={editingBanner}
              onCancel={() => {
                setView('list');
                setDraftPlacement(null);
                setEditingBanner(null);
              }}
              onSaved={async () => {
                const wasEditing = editingBanner != null;
                setView('list');
                setDraftPlacement(null);
                setEditingBanner(null);
                await loadBanners();
                setToast({
                  msg: wasEditing ? 'Změny uloženy' : 'Banner uložen',
                  kind: 'success',
                });
              }}
              onError={(msg) => setToast({ msg, kind: 'error' })}
            />
          )}
          {view === 'settings' && (
            <PlacementSettingsView
              settings={settings}
              catalogStatus={catalogStatus}
              onEdit={setEditingSelectorFor}
              onSync={syncCatalog}
              onShowStocks={() => setShowingStocks(true)}
              onBack={() => setView('list')}
            />
          )}
        </>
      )}

      {tab === 'offers' && (
        <>
          {offersView === 'list' && (
            <OffersList
              offers={offers}
              isAdmin={isAdmin}
              onNew={startNewOffer}
              onEdit={startEditOffer}
              onDelete={deleteOffer}
              onToggleActive={toggleOfferActive}
              onCreateCombination={setCombinationFor}
              onOpenStats={() => setStatsOpen(true)}
              onOpenSettings={
                isAdmin
                  ? () => {
                      setTab('banners');
                      setView('settings');
                    }
                  : null
              }
            />
          )}
          {statsOpen && <OfferStatsModal onClose={() => setStatsOpen(false)} />}
          {offersView === 'new' && (
            <OfferWizard
              key={editingOffer ? `edit-${editingOffer.id}` : 'new'}
              isAdmin={isAdmin}
              existingOffer={editingOffer}
              onCancel={() => {
                setOffersView('list');
                setEditingOffer(null);
              }}
              onSaved={async () => {
                const wasEditing = editingOffer != null;
                setOffersView('list');
                setEditingOffer(null);
                await loadOffers();
                setToast({
                  msg: wasEditing ? 'Změny uloženy' : 'Nabídka uložena',
                  kind: 'success',
                });
              }}
              onError={(msg) => setToast({ msg, kind: 'error' })}
            />
          )}
        </>
      )}

      {editingSelectorFor && (
        <SelectorModal
          placement={editingSelectorFor}
          currentSelector={settings[editingSelectorFor]?.css_selector ?? null}
          onSave={saveSelector}
          onClose={() => setEditingSelectorFor(null)}
        />
      )}

      {showingStocks && <VariantStocksModal onClose={() => setShowingStocks(false)} />}

      {combinationFor && (
        <QuickCombinationModal
          sourceOffer={combinationFor}
          // IDs produktů, které už jsou kotvou v některé z kombinací stejné sady
          // — modal je vyfiltruje, aby nešlo vytvořit duplicitní kombinaci.
          existingAnchorIds={(() => {
            const key = offerGroupKey(combinationFor);
            if (key === null) return [];
            return offers
              .filter((o) => offerGroupKey(o) === key)
              .flatMap((o) => o.trigger_product_ids);
          })()}
          onClose={() => setCombinationFor(null)}
          onCreated={async () => {
            setCombinationFor(null);
            await loadOffers();
            setToast({ msg: 'Kombinace vytvořena', kind: 'success' });
          }}
          onError={(msg) => setToast({ msg, kind: 'error' })}
        />
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  const base = 'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer';
  const cls = active
    ? 'border-primary text-primary'
    : 'border-transparent text-text-secondary hover:text-text';
  return (
    <button type="button" onClick={onClick} className={`${base} ${cls}`}>
      {children}
    </button>
  );
}

function BannersList({
  banners,
  isAdmin,
  onNew,
  onEdit,
  onDelete,
  onToggleActive,
  onOpenSettings,
}: {
  banners: BannerDto[];
  isAdmin: boolean;
  onNew: () => void;
  onEdit: (b: BannerDto) => void;
  onDelete: (id: number, name: string) => void;
  onToggleActive: (id: number, active: boolean) => void;
  onOpenSettings: (() => void) | null;
}) {
  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-text">Bannery</h2>
          <p className="text-xs text-text-muted mt-0.5">
            Vlastní obrázky vykreslené na zvolených místech e-shopu.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onOpenSettings && (
            <button
              type="button"
              onClick={onOpenSettings}
              className="px-3 py-2 bg-surface border border-border text-text-secondary hover:text-text hover:border-primary/40 text-sm font-medium rounded-lg transition-colors cursor-pointer inline-flex items-center gap-1.5"
            >
              <GearIcon />
              Nastavení
            </button>
          )}
          <button
            type="button"
            onClick={onNew}
            className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors cursor-pointer"
          >
            + Vytvořit banner
          </button>
        </div>
      </div>

      {banners.length === 0 ? (
        <div className="bg-surface rounded-xl border border-border p-10 text-center">
          <div className="text-text-muted text-sm">Zatím tu nejsou žádné bannery.</div>
          <div className="text-text-muted text-xs mt-1">
            Klikni na „Vytvořit banner" a začni první kampaní.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {banners.map((b) => (
            <BannerCard
              key={b.id}
              banner={b}
              isAdmin={isAdmin}
              onEdit={() => onEdit(b)}
              onDelete={() => onDelete(b.id, b.internal_name)}
              onToggleActive={(active) => onToggleActive(b.id, active)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function BannerCard({
  banner,
  isAdmin,
  onEdit,
  onDelete,
  onToggleActive,
}: {
  banner: BannerDto;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: (active: boolean) => void;
}) {
  const placementLabel =
    PLACEMENTS.find((p) => p.id === banner.placement)?.label ?? banner.placement;
  const triggerLabel = describeTrigger(banner);
  const thumb = banner.image_desktop_url ?? banner.image_mobile_url;
  const ctr = banner.impressions > 0 ? (banner.clicks / banner.impressions) * 100 : null;
  return (
    <div
      className={`bg-surface border border-border rounded-xl overflow-hidden flex flex-col ${
        banner.active ? '' : 'opacity-60'
      }`}
    >
      <div className="aspect-[16/9] bg-bg flex items-center justify-center overflow-hidden p-2 relative">
        {banner.banner_type === 'produkt_tydne' && (
          <span className="absolute top-1.5 left-1.5 z-10 text-[10px] font-bold uppercase tracking-wide bg-[#ff2159] text-white px-1.5 py-0.5 rounded">
            🏆 Produkt týdne
          </span>
        )}
        {thumb ? (
          <img
            src={thumb}
            alt={banner.alt_text ?? ''}
            className="max-w-full max-h-full object-contain"
          />
        ) : banner.banner_type === 'review' ? (
          <div
            className="w-full h-full flex flex-col items-center justify-center text-center px-3"
            style={{ background: '#fff9e3', color: '#16243a' }}
          >
            <div className="flex gap-0.5 mb-1.5 text-lg leading-none" style={{ color: '#f59e0b' }}>
              {'★'.repeat(Math.min(5, Math.max(0, banner.review_rating ?? 0))) || '★★★★★'}
            </div>
            {banner.review_author && (
              <div className="text-sm font-semibold leading-tight">{banner.review_author}</div>
            )}
            {!banner.review_author && (
              <div className="text-xs text-text-muted">{banner.review_title ?? 'Hodnocení'}</div>
            )}
          </div>
        ) : (
          <div className="text-xs text-text-muted">bez obrázku</div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-1">
        {/* TOP: pozice (CAPS) + případně badge */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <div
            className="text-base font-bold text-text uppercase tracking-wide leading-tight"
            title={placementLabel}
          >
            {placementLabel}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!banner.active && (
              <span className="text-[10px] font-semibold text-text-muted bg-bg border border-border px-1.5 py-0.5 rounded">
                Skryto
              </span>
            )}
            <span className="text-[10px] font-semibold text-text-muted bg-bg border border-border px-1.5 py-0.5 rounded">
              prio {banner.priority}
            </span>
          </div>
        </div>

        {/* Konec platnosti — větší font, aby šlo rychle přečíst */}
        {banner.end_at ? (
          <div className="text-sm font-semibold text-text">Končí {fmtDateTime(banner.end_at)}</div>
        ) : (
          <div className="text-sm font-semibold text-text-muted">Bez konce platnosti</div>
        )}
        {banner.start_at && banner.start_at > Date.now() && (
          <div className="text-xs text-text-muted">Začíná {fmtDateTime(banner.start_at)}</div>
        )}

        {/* Sekundární meta */}
        <div className="text-xs text-text-muted truncate mt-2" title={banner.internal_name}>
          {banner.internal_name}
        </div>
        <div className="text-xs text-text-muted truncate">{triggerLabel}</div>

        {/* Statistiky */}
        <div className="mt-2 flex items-center gap-3 text-[11px] text-text-muted">
          <span title="Počet zobrazení">
            <strong className="text-text">{banner.impressions}</strong> zobrazení
          </span>
          <span title="Počet kliknutí">
            <strong className="text-text">{banner.clicks}</strong> kliků
          </span>
          {ctr !== null && (
            <span title="Click-through rate">
              CTR <strong className="text-text">{ctr.toFixed(1)}%</strong>
            </span>
          )}
        </div>

        {isAdmin && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <ActiveSwitch active={banner.active} onChange={onToggleActive} />
            <button
              type="button"
              onClick={onEdit}
              className="text-xs text-text-secondary hover:text-primary border border-border rounded-md px-2.5 py-1 cursor-pointer"
            >
              Upravit
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="text-xs text-text-secondary hover:text-danger border border-border rounded-md px-2.5 py-1 cursor-pointer"
            >
              Smazat
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ActiveSwitch({
  active,
  onChange,
}: { active: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      onClick={() => onChange(!active)}
      title={active ? 'Aktivní (klikni pro deaktivaci)' : 'Neaktivní (klikni pro aktivaci)'}
      className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors cursor-pointer shrink-0 ${
        active ? 'bg-primary' : 'bg-border'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-4 w-4 bg-white rounded-full shadow transition-transform ${
          active ? 'translate-x-4' : ''
        }`}
      />
      <span className="sr-only">{active ? 'Aktivní' : 'Neaktivní'}</span>
    </button>
  );
}

function describeTrigger(b: BannerDto): string {
  switch (b.trigger_type) {
    case 'products':
      return `Produkty (${b.trigger_product_ids.length})`;
    case 'category':
      return `Kategorie (${b.trigger_category_guids.length})`;
    case 'flag':
      return `Příznaky (${b.trigger_flag_codes.length})`;
    case 'everywhere':
      return 'Všude';
  }
}

// ─── Nabídky (cross-sell) — admin UI ────────────────────────────────────

/**
 * Skupiny kombinací — dvě nabídky jsou ze stejné „sady" produktů, pokud
 * pro stejný placement zahrnují stejnou množinu (trigger + doporučené).
 * Příklad sady {K1056, CE1001, T1089}:
 *   - Offer A: trigger K1056, rec [CE1001, T1089]
 *   - Offer B: trigger CE1001, rec [K1056, T1089]
 * Obě patří do skupiny 2/2. Skupinování má smysl jen pro trigger=products
 * (kategorie/flag/everywhere nemají „kotvu", nejsou kombinace).
 */
/**
 * Auto-generuje interní název nabídky z trigger kontextu. Pole je v UI
 * skryté — uživateli stačí kontext (kotva, kategorie, příznak…), který
 * je sám smysluplnou identifikací nabídky.
 */
function buildOfferInternalName(
  triggerType: TriggerType,
  triggerProducts: ProductLite[],
  categoryGuids: string[],
  flagCodes: string[],
  placement: string,
): string {
  if (triggerType === 'products') {
    const first = triggerProducts[0];
    if (first) {
      const code = first.parent_code ?? String(first.shopitem_id);
      return `${code} - ${first.name}`;
    }
    return 'Nabídka (bez kotvy)';
  }
  if (triggerType === 'category') {
    return categoryGuids.length > 0 ? `Kategorie (${categoryGuids.length})` : 'Kategorie';
  }
  if (triggerType === 'flag') {
    const codes = flagCodes.slice(0, 3).join(', ');
    return codes ? `Příznak: ${codes}` : 'Příznak';
  }
  if (triggerType === 'everywhere') {
    return `Všude (${placement})`;
  }
  return 'Nabídka';
}

function offerGroupKey(o: OfferDto): string | null {
  if (o.trigger_type !== 'products') return null;
  const setIds = Array.from(new Set([...o.trigger_product_ids, ...o.product_ids])).sort(
    (a, b) => a - b,
  );
  if (setIds.length === 0) return null;
  return `${o.placement}|${setIds.join(',')}`;
}

function computeOfferGroups(
  offers: OfferDto[],
): Map<number, { size: number; setSize: number; siblings: OfferDto[] }> {
  const groups = new Map<string, OfferDto[]>();
  for (const o of offers) {
    const k = offerGroupKey(o);
    if (k === null) continue;
    const arr = groups.get(k) ?? [];
    arr.push(o);
    groups.set(k, arr);
  }
  const meta = new Map<number, { size: number; setSize: number; siblings: OfferDto[] }>();
  for (const arr of groups.values()) {
    arr.sort((a, b) => a.created_at - b.created_at);
    // setSize = počet unikátních produktů ve sjednocení trigger+rec (= max možných
    // kombinací, kdy každý produkt může být kotvou). Pro všechny v skupině stejné.
    const first = arr[0];
    const setSize = first ? new Set([...first.trigger_product_ids, ...first.product_ids]).size : 0;
    for (const o of arr) {
      meta.set(o.id, { size: arr.length, setSize, siblings: arr });
    }
  }
  return meta;
}

function OffersList({
  offers,
  isAdmin,
  onNew,
  onEdit,
  onDelete,
  onToggleActive,
  onCreateCombination,
  onOpenStats,
  onOpenSettings,
}: {
  offers: OfferDto[];
  isAdmin: boolean;
  onNew: () => void;
  onEdit: (o: OfferDto) => void;
  onDelete: (id: number, name: string) => void;
  onToggleActive: (id: number, active: boolean) => void;
  onCreateCombination: (o: OfferDto) => void;
  onOpenStats: () => void;
  onOpenSettings: (() => void) | null;
}) {
  // Skupinová meta počítáme ze VŠECH nabídek, ne z filtru — badge "2/3
  // kombinací" musí odrážet realitu i když filtr něco skrývá.
  const groupMeta = computeOfferGroups(offers);

  // Vyhledávání — match proti kotvě i doporučeným (přímé i nepřímé propojení).
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const filtered = q
    ? offers.filter((o) => {
        const matchProduct = (p: { parent_code: string | null; name: string } | null): boolean => {
          if (!p) return false;
          if (p.parent_code && p.parent_code.toLowerCase().includes(q)) return true;
          if (p.name.toLowerCase().includes(q)) return true;
          return false;
        };
        if (matchProduct(o.trigger_product)) return true;
        if (o.recommended_products.some(matchProduct)) return true;
        if (o.internal_name.toLowerCase().includes(q)) return true;
        return false;
      })
    : offers;
  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-text">Nabídky</h2>
          <p className="text-xs text-text-muted mt-0.5">
            Cross-sell — doporučené produkty na detailu a v košíku.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpenStats}
            className="px-3 py-2 bg-surface border border-border text-text-secondary hover:text-text hover:border-primary/40 text-sm font-medium rounded-lg transition-colors cursor-pointer"
            title="Souhrnná statistika všech nabídek s vývojem v čase"
          >
            Statistika
          </button>
          {onOpenSettings && (
            <button
              type="button"
              onClick={onOpenSettings}
              className="px-3 py-2 bg-surface border border-border text-text-secondary hover:text-text hover:border-primary/40 text-sm font-medium rounded-lg transition-colors cursor-pointer inline-flex items-center gap-1.5"
            >
              <GearIcon />
              Nastavení
            </button>
          )}
          <button
            type="button"
            onClick={onNew}
            className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors cursor-pointer"
          >
            + Vytvořit nabídku
          </button>
        </div>
      </div>

      {/* Vyhledávání — podle názvu i kódu, najde přímé i nepřímé propojení
          (produkt je buď kotva NEBO doporučený). */}
      {offers.length > 0 && (
        <div className="mb-4">
          <div className="relative max-w-md">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Hledat podle názvu nebo kódu produktu…"
              className="w-full text-sm text-text bg-surface border border-border rounded-lg pl-9 pr-9 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Vyčistit"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text text-base leading-none cursor-pointer px-1"
              >
                ×
              </button>
            )}
          </div>
          {q && (
            <div className="text-xs text-text-muted mt-1.5">
              {filtered.length === 0
                ? 'Žádná nabídka neodpovídá.'
                : `Nalezeno ${filtered.length} z ${offers.length} nabídek.`}
            </div>
          )}
        </div>
      )}

      {offers.length === 0 ? (
        <div className="bg-surface rounded-xl border border-border p-10 text-center">
          <div className="text-text-muted text-sm">Zatím tu nejsou žádné nabídky.</div>
          <div className="text-text-muted text-xs mt-1">
            Klikni na „Vytvořit nabídku" a začni první cross-sell.
          </div>
        </div>
      ) : filtered.length === 0 ? null : (
        <div className="flex flex-col gap-2">
          {filtered.map((o) => (
            <OfferCard
              key={o.id}
              offer={o}
              isAdmin={isAdmin}
              groupInfo={groupMeta.get(o.id) ?? null}
              onEdit={() => onEdit(o)}
              onDelete={() => onDelete(o.id, o.internal_name)}
              onToggleActive={(active) => onToggleActive(o.id, active)}
              onCreateCombination={() => onCreateCombination(o)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function OfferCard({
  offer,
  isAdmin,
  groupInfo,
  onEdit,
  onDelete,
  onToggleActive,
  onCreateCombination,
}: {
  offer: OfferDto;
  isAdmin: boolean;
  groupInfo: { size: number; setSize: number } | null;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: (active: boolean) => void;
  onCreateCombination: () => void;
}) {
  const placementLabel =
    OFFER_PLACEMENTS.find((p) => p.id === offer.placement)?.label ?? offer.placement;
  // CTR = klik na produktovou kartu / impression; cartRate = klik "Do košíku" / impression.
  // cartRate je vlastní konverze cross-sellu (přidání rovnou bez nutnosti přejít na detail).
  const ctr = offer.impressions > 0 ? (offer.clicks / offer.impressions) * 100 : null;
  const cartRate = offer.impressions > 0 ? (offer.cart_clicks / offer.impressions) * 100 : null;
  const triggerLabel = describeOfferTrigger(offer);
  const trigger = offer.trigger_product;
  const recs = offer.recommended_products;
  return (
    <div
      className={`bg-surface border border-border rounded-lg p-3 flex items-stretch gap-3 ${
        offer.active ? '' : 'opacity-60'
      }`}
    >
      {/* 1) Trigger — foto + kód + název; pro non-products trigger jen popisek. */}
      <div className="flex items-center gap-3 shrink-0 w-64">
        <div className="w-14 h-14 bg-bg border border-border rounded-md flex items-center justify-center overflow-hidden shrink-0">
          {trigger?.image_url ? (
            <img
              src={trigger.image_url}
              alt={trigger.name}
              className="max-w-full max-h-full object-contain"
              title={trigger.name}
            />
          ) : (
            <div className="text-[9px] text-text-muted text-center px-1 leading-tight">
              {offer.trigger_type === 'everywhere'
                ? 'Vše'
                : offer.trigger_type === 'category'
                  ? 'Kateg.'
                  : offer.trigger_type === 'flag'
                    ? 'Flag'
                    : '—'}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <div
            className="text-[10px] font-semibold uppercase tracking-wide text-text-muted truncate"
            title={placementLabel}
          >
            {placementLabel}
          </div>
          {trigger ? (
            <>
              <div className="text-sm font-mono text-text leading-tight" title={trigger.name}>
                {trigger.parent_code ?? '—'}
              </div>
              <div className="text-xs text-text-secondary truncate" title={trigger.name}>
                {trigger.name}
              </div>
            </>
          ) : (
            <div className="text-xs text-text-secondary truncate" title={triggerLabel}>
              {triggerLabel}
            </div>
          )}
          {!offer.active && (
            <span className="text-[9px] font-semibold text-text-muted bg-bg border border-border px-1 py-0.5 rounded w-fit mt-0.5">
              Skryto
            </span>
          )}
        </div>
      </div>

      {/* 2) Doporučené produkty — řada miniatur + per-product stats. */}
      <div className="flex gap-2 overflow-x-auto flex-1 min-w-0 items-start py-0.5">
        {recs.length === 0 ? (
          <div className="text-xs text-text-muted self-center">žádné produkty</div>
        ) : (
          recs.map((rec) => (
            <div
              key={rec.shopitem_id}
              className="shrink-0 w-20 flex flex-col items-center gap-0.5"
              title={rec.name}
            >
              <div className="w-14 h-14 bg-bg border border-border rounded-md flex items-center justify-center overflow-hidden">
                {rec.image_url ? (
                  <img
                    src={rec.image_url}
                    alt={rec.name}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="text-[9px] text-text-muted">—</div>
                )}
              </div>
              <div className="text-[10px] font-mono text-text-muted truncate w-full text-center">
                {rec.parent_code ?? '—'}
              </div>
              <div className="text-[10px] text-text-muted flex items-center gap-1">
                <span title="Kliků na produkt">{rec.product_clicks}</span>
                <span>/</span>
                <span className="font-semibold text-primary" title="Do košíku">
                  {rec.cart_clicks}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 3) Kombinace — badge "size/setSize" + tlačítko na vytvoření další.
              size = kolik kombinací existuje, setSize = kolik je maximálně možných
              (počet unikátních produktů v sadě = každý může být kotvou).
              Skupinování má smysl jen pro trigger=products. */}
      {offer.trigger_type === 'products' && isAdmin && (
        <div className="shrink-0 flex flex-col items-center gap-1.5 self-center w-28">
          {groupInfo && groupInfo.setSize > 1 && (
            <div
              className={`text-sm font-bold rounded-md px-3 py-1 border whitespace-nowrap ${
                groupInfo.size >= groupInfo.setSize
                  ? 'bg-success-light text-success border-success/40'
                  : 'bg-warning-light text-warning border-warning/40'
              }`}
              title={
                groupInfo.size >= groupInfo.setSize
                  ? `Sada je kompletní — existují všechny ${groupInfo.setSize} kombinace`
                  : `Existují ${groupInfo.size} ze ${groupInfo.setSize} možných kombinací — můžeš přidat další`
              }
            >
              {groupInfo.size}/{groupInfo.setSize} kombinací
            </div>
          )}
          {(!groupInfo || groupInfo.size < groupInfo.setSize) && (
            <button
              type="button"
              onClick={onCreateCombination}
              className="text-[11px] text-primary hover:text-primary-dark border border-primary/40 hover:border-primary rounded-md px-2 py-1 cursor-pointer whitespace-nowrap"
              title="Vytvořit další kombinaci — vezmi některý z doporučených produktů jako novou kotvu"
            >
              + Další kombinace
            </button>
          )}
        </div>
      )}

      {/* 4) Offer-level funnel — kompaktní sloupec. */}
      <div className="shrink-0 w-32 flex flex-col gap-0.5 text-[11px] text-text-muted self-center">
        <div title="Zobrazení">
          <strong className="text-text">{offer.impressions}</strong> zobrazení
        </div>
        <div title="Klik na produkt">
          <strong className="text-text">{offer.clicks}</strong> klik / {offer.cart_clicks} košík
        </div>
        {ctr !== null && (
          <div title="Click-through rate">
            CTR <strong className="text-text">{ctr.toFixed(1)}%</strong>
          </div>
        )}
        {cartRate !== null && (
          <div title="Konverze do košíku" className="text-primary">
            Konv. <strong>{cartRate.toFixed(1)}%</strong>
          </div>
        )}
      </div>

      {/* 4) Akce — switch + Upravit + Smazat. */}
      {isAdmin && (
        <div className="shrink-0 flex items-center gap-2 self-center">
          <ActiveSwitch active={offer.active} onChange={onToggleActive} />
          <button
            type="button"
            onClick={onEdit}
            className="text-xs text-text-secondary hover:text-primary border border-border rounded-md px-2.5 py-1 cursor-pointer"
          >
            Upravit
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-xs text-text-secondary hover:text-danger border border-border rounded-md px-2.5 py-1 cursor-pointer"
          >
            Smazat
          </button>
        </div>
      )}
    </div>
  );
}

function describeOfferTrigger(o: OfferDto): string {
  switch (o.trigger_type) {
    case 'products':
      return `Spouštěč: produkty (${o.trigger_product_ids.length})`;
    case 'category':
      return `Spouštěč: kategorie (${o.trigger_category_guids.length})`;
    case 'flag':
      return `Spouštěč: příznaky (${o.trigger_flag_codes.length})`;
    case 'everywhere':
      return 'Spouštěč: všude';
  }
}

function NewBannerWizard({
  placement,
  onPickPlacement,
  settings,
  isAdmin,
  existingBanner,
  onCancel,
  onSaved,
  onError,
}: {
  placement: BannerPlacement | null;
  onPickPlacement: (p: BannerPlacement) => void;
  settings: PlacementSettings;
  isAdmin: boolean;
  existingBanner: BannerDto | null;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
  onError: (msg: string) => void;
}) {
  const isEdit = existingBanner != null;

  // Step 2 (Spouštěč) state — žije uvnitř wizardu, na Zrušit + reopen je čistý.
  // V edit módu pre-fill z existingBanner (lazy init).
  const [triggerType, setTriggerType] = useState<TriggerType | null>(
    () => existingBanner?.trigger_type ?? null,
  );
  const [selectedProducts, setSelectedProducts] = useState<ProductLite[]>([]);
  const [selectedCategoryGuids, setSelectedCategoryGuids] = useState<string[]>(
    () => existingBanner?.trigger_category_guids ?? [],
  );
  const [selectedFlagCodes, setSelectedFlagCodes] = useState<string[]>(
    () => existingBanner?.trigger_flag_codes ?? [],
  );

  // V edit módu fetchneme detaily produktů podle ID, abychom mohli ukázat chips.
  useEffect(() => {
    if (!existingBanner || existingBanner.trigger_type !== 'products') return;
    const ids = existingBanner.trigger_product_ids;
    if (ids.length === 0) return;
    (async () => {
      try {
        const params = new URLSearchParams({ ids: ids.join(',') });
        const data = await apiFetch<{ products: ProductLite[] }>(
          `/api/propojeni/products?${params.toString()}`,
        );
        setSelectedProducts(data.products);
      } catch {
        /* pokud sync feedu produkt smazal, jen ho neukážeme */
      }
    })();
  }, [existingBanner]);

  // Pokud uživatel změní placement na takový, kde už zvolený trigger neplatí,
  // trigger resetneme (a vyčistíme jeho data, ať se neposílají do API).
  useEffect(() => {
    if (!placement || !triggerType) return;
    const allowed = allowedTriggersFor(placement);
    if (!allowed.includes(triggerType)) {
      setTriggerType(null);
      setSelectedProducts([]);
      setSelectedFlagCodes([]);
    }
  }, [placement, triggerType]);
  // Step 3 (Grafika)
  const [desktopImage, setDesktopImage] = useState<UploadedImage | null>(() =>
    existingBanner?.image_desktop_url
      ? existingImageFromUrl(existingBanner.image_desktop_url, 'desktop')
      : null,
  );
  const [mobileImage, setMobileImage] = useState<UploadedImage | null>(() =>
    existingBanner?.image_mobile_url
      ? existingImageFromUrl(existingBanner.image_mobile_url, 'mobile')
      : null,
  );
  // Step 4 (Detail nabídky)
  const [targetUrl, setTargetUrl] = useState(() => existingBanner?.target_url ?? '');
  const [targetBlank, setTargetBlank] = useState(() => existingBanner?.target_blank ?? false);
  const [altText, setAltText] = useState(() => existingBanner?.alt_text ?? '');
  const [internalName, setInternalName] = useState(() => existingBanner?.internal_name ?? '');
  const [priority, setPriority] = useState(() => String(existingBanner?.priority ?? 0));
  const [startDate, setStartDate] = useState(() => msToDateInput(existingBanner?.start_at));
  const [startTime, setStartTime] = useState(() => msToTimeInput(existingBanner?.start_at));
  const [endDate, setEndDate] = useState(() => msToDateInput(existingBanner?.end_at));
  const [endTime, setEndTime] = useState(() => msToTimeInput(existingBanner?.end_at));
  // grid_position — relevantní jen pro 'category-products' (pozice v gridu, 1-based).
  const [gridPosition, setGridPosition] = useState(() =>
    existingBanner?.grid_position != null ? String(existingBanner.grid_position) : '',
  );
  // banner_type — 'image' (default), 'review' (jen category-products) nebo
  // 'produkt_tydne' (jen homepage-main).
  const [bannerType, setBannerType] = useState<'image' | 'review' | 'produkt_tydne'>(
    () => existingBanner?.banner_type ?? 'image',
  );
  const [reviewRating, setReviewRating] = useState(() =>
    existingBanner?.review_rating != null ? String(existingBanner.review_rating) : '5',
  );
  const [reviewTitle, setReviewTitle] = useState(() => existingBanner?.review_title ?? '');
  const [reviewBody, setReviewBody] = useState(() => existingBanner?.review_body ?? '');
  const [reviewAuthor, setReviewAuthor] = useState(() => existingBanner?.review_author ?? '');
  // Produkt týdne pole (volný text).
  const [ptPriceOriginal, setPtPriceOriginal] = useState(
    () => existingBanner?.pt_price_original ?? '',
  );
  const [ptPriceNew, setPtPriceNew] = useState(() => existingBanner?.pt_price_new ?? '');
  const [ptDiscount, setPtDiscount] = useState(() => existingBanner?.pt_discount ?? '');
  const [saving, setSaving] = useState(false);
  // „Uložit a přidat další" stav (jen create + category-products): po uložení
  // resetneme image/position/time/internal_name, ale ponecháme placement/trigger/priority.
  const [savingAndContinue, setSavingAndContinue] = useState(false);

  const isCategoryProducts = placement === 'category-products';
  const isHomepage = placement === 'homepage-main';
  const isReview = bannerType === 'review';
  const isProduktTydne = bannerType === 'produkt_tydne';
  // Dostupné typy banneru dle umístění.
  const typeOptions: Array<'image' | 'review' | 'produkt_tydne'> = isCategoryProducts
    ? ['image', 'review']
    : isHomepage
      ? ['image', 'produkt_tydne']
      : ['image'];
  const gridPositionNum = Number.parseInt(gridPosition, 10);
  const gridPositionValid =
    !isCategoryProducts ||
    (Number.isFinite(gridPositionNum) && gridPositionNum >= 1 && gridPositionNum <= 100);
  const contentValid = isReview
    ? reviewBody.trim().length > 0
    : isProduktTydne
      ? (!!desktopImage || !!mobileImage) && ptPriceNew.trim().length > 0
      : !!desktopImage || !!mobileImage;

  // Když nové umístění nepodporuje aktuální typ banneru, vrať se na „Obrázek".
  useEffect(() => {
    const valid =
      placement === 'category-products'
        ? ['image', 'review']
        : placement === 'homepage-main'
          ? ['image', 'produkt_tydne']
          : ['image'];
    setBannerType((bt) => (valid.includes(bt) ? bt : 'image'));
  }, [placement]);

  const canSave =
    !!placement &&
    !!triggerType &&
    contentValid &&
    internalName.trim().length > 0 &&
    gridPositionValid &&
    (triggerType !== 'products' || selectedProducts.length > 0) &&
    (triggerType !== 'category' || selectedCategoryGuids.length > 0) &&
    (triggerType !== 'flag' || selectedFlagCodes.length > 0);

  // Diagnostika: vyrobí čitelnou zprávu, co ještě uživateli ve wizardu chybí —
  // ukážeme ji jako tooltip + drobný text vedle disabled tlačítek.
  function missingChecks(): string[] {
    const out: string[] = [];
    if (!placement) out.push('Vyber umístění (sekce 1)');
    if (!triggerType) out.push('Vyber spouštěč (sekce 2)');
    if (triggerType === 'products' && selectedProducts.length === 0) {
      out.push('Vyber alespoň jeden produkt (sekce 2)');
    }
    if (triggerType === 'category' && selectedCategoryGuids.length === 0) {
      out.push('Vyber alespoň jednu kategorii (sekce 2)');
    }
    if (triggerType === 'flag' && selectedFlagCodes.length === 0) {
      out.push('Vyber alespoň jeden příznak (sekce 2)');
    }
    if (isReview) {
      if (reviewBody.trim().length === 0) out.push('Vyplň text recenze (sekce 3)');
    } else if (isProduktTydne) {
      if (!desktopImage && !mobileImage) out.push('Nahraj grafiku (sekce 3)');
      if (ptPriceNew.trim().length === 0) out.push('Vyplň novou cenu (sekce 3)');
    } else if (!desktopImage && !mobileImage) {
      out.push('Nahraj grafiku (sekce 3)');
    }
    if (internalName.trim().length === 0) out.push('Vyplň interní název');
    if (!gridPositionValid) out.push('Vyplň pozici v kategorii (1–100)');
    return out;
  }

  function resetBannerLevelFields() {
    setDesktopImage(null);
    setMobileImage(null);
    setTargetUrl('');
    setTargetBlank(false);
    setAltText('');
    setInternalName('');
    setStartDate('');
    setStartTime('');
    setEndDate('');
    setEndTime('');
    setGridPosition('');
    setReviewTitle('');
    setReviewBody('');
    setReviewAuthor('');
    setPtPriceOriginal('');
    setPtPriceNew('');
    setPtDiscount('');
    // Hvězdy + typ ponecháme — uživatel si je nastavil pro celou sadu.
  }

  async function handleSave(opts?: { addAnother?: boolean }) {
    if (!canSave || !placement || !triggerType) return;
    const addAnother = opts?.addAnother === true;
    if (addAnother) setSavingAndContinue(true);
    else setSaving(true);
    try {
      const startMs = composeDateTime(startDate, startTime, false);
      const endMs = composeDateTime(endDate, endTime, true);
      const reviewRatingNum = Number.parseInt(reviewRating, 10);
      const body = {
        placement,
        banner_type: bannerType,
        trigger_type: triggerType,
        trigger_product_ids: selectedProducts.map((p) => p.shopitem_id),
        trigger_category_guids: selectedCategoryGuids,
        trigger_flag_codes: selectedFlagCodes,
        // U review banneru obrázky neposíláme.
        image_desktop: isReview ? null : imageForApi(desktopImage),
        image_mobile: isReview ? null : imageForApi(mobileImage),
        target_url: targetUrl.trim() || null,
        target_blank: targetBlank,
        alt_text: altText.trim() || null,
        internal_name: internalName.trim(),
        priority: Number.parseInt(priority, 10) || 0,
        start_at: startMs,
        end_at: endMs,
        grid_position: isCategoryProducts ? gridPositionNum : null,
        review_rating: isReview && Number.isFinite(reviewRatingNum) ? reviewRatingNum : null,
        review_title: isReview ? reviewTitle.trim() || null : null,
        review_body: isReview ? reviewBody.trim() || null : null,
        review_author: isReview ? reviewAuthor.trim() || null : null,
        pt_price_original: isProduktTydne ? ptPriceOriginal.trim() || null : null,
        pt_price_new: isProduktTydne ? ptPriceNew.trim() || null : null,
        pt_discount: isProduktTydne ? ptDiscount.trim() || null : null,
        active: existingBanner?.active ?? true,
      };
      if (isEdit && existingBanner) {
        await apiFetch(`/api/upsell/banners/${existingBanner.id}`, {
          method: 'PATCH',
          body,
        });
      } else {
        await apiFetch('/api/upsell/banners', { method: 'POST', body });
      }
      if (addAnother) {
        // Sdílené pole (placement, trigger, priority) zachováme; resetneme
        // jen banner-level. UI zůstane otevřený s prázdným banner blokem.
        resetBannerLevelFields();
      } else {
        await onSaved();
      }
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
      setSavingAndContinue(false);
    }
  }

  return (
    <div className="bg-surface rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-text">
          {isEdit ? `Upravit banner — ${existingBanner?.internal_name}` : 'Nový banner'}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-text-muted hover:text-text cursor-pointer"
        >
          Zrušit
        </button>
      </div>
      <p className="text-sm text-text-secondary mb-6">
        Zobrazí výraznou nabídku nebo sdělení na e-shopu. Využijte pro kampaně, slevy nebo navigaci
        na důležité produkty.
      </p>

      <section>
        <SectionHeader title="1. Umístění" />

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {PLACEMENTS.map((p) => {
            const selected = placement === p.id;
            const selector = settings[p.id]?.css_selector ?? null;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onPickPlacement(p.id)}
                className={`text-left border-2 rounded-lg overflow-hidden transition-all cursor-pointer ${
                  selected ? 'border-primary shadow-card' : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="bg-bg aspect-[4/3] flex items-center justify-center overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.label}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div
                  className={`px-3 py-2 text-xs font-medium border-t ${
                    selected
                      ? 'bg-primary-50 text-primary border-primary/30'
                      : 'bg-surface text-text-secondary border-border'
                  }`}
                >
                  <div>{p.label}</div>
                  <div className="mt-1 text-[11px] font-mono truncate text-text-muted">
                    {isEarPlacement(p.id) ? (
                      <span className="italic">selektor není potřeba</span>
                    ) : selector ? (
                      selector
                    ) : (
                      <span className="italic">selektor neuložen</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-text-muted mt-3">
          CSS selektor pro každé umístění (kam se má banner v šabloně e-shopu vložit) nastav v
          „Nastavení" v hlavičce seznamu bannerů.
        </p>
      </section>

      {placement && (
        <section className="mt-8">
          <SectionHeader title="2. Spouštěč" />
          <p className="text-sm text-text-secondary mb-3">
            Vyber, kde se má banner zobrazit.
            {placement !== 'product-photos' && (
              <span className="text-text-muted">
                {' '}
                Pro kategoriová umístění nejsou „Konkrétní produkty" ani „Příznak" k dispozici —
                kategoriová stránka nemá kontext konkrétního produktu.
              </span>
            )}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {TRIGGER_TYPES.filter((t) => allowedTriggersFor(placement).includes(t.id)).map((t) => {
              const selected = triggerType === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTriggerType(t.id)}
                  className={`text-left p-4 border-2 rounded-lg transition-all cursor-pointer ${
                    selected
                      ? 'border-primary bg-primary-50 shadow-card'
                      : 'border-border bg-surface hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        selected ? 'border-primary' : 'border-border'
                      }`}
                    >
                      {selected && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <div className="text-sm font-medium text-text">{t.label}</div>
                  </div>
                  <p className="text-xs text-text-muted mt-2 leading-relaxed">{t.description}</p>
                </button>
              );
            })}
          </div>

          {triggerType && (
            <div className="mt-4">
              {triggerType === 'products' && (
                <ProductsPicker selected={selectedProducts} onChange={setSelectedProducts} />
              )}
              {triggerType === 'category' && (
                <CategoryPicker
                  selectedGuids={selectedCategoryGuids}
                  onChange={setSelectedCategoryGuids}
                />
              )}
              {triggerType === 'flag' && (
                <FlagsPicker selectedCodes={selectedFlagCodes} onChange={setSelectedFlagCodes} />
              )}
              {triggerType === 'everywhere' && (
                <div className="p-3 bg-bg border border-border rounded-lg text-sm text-text-secondary">
                  Banner se zobrazí na všech stránkách, kde dané umístění existuje. Žádný další
                  výběr není potřeba.
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {placement && triggerType && typeOptions.length > 1 && (
        <section className="mt-8">
          <SectionHeader title="3. Typ obsahu" />
          <p className="text-sm text-text-secondary mb-3">
            {isHomepage
              ? 'Banner na úvodní stránce může být klasický obrázek, nebo „Produkt týdne" — obrázek doplněný cenovým blokem a odpočtem do konce akce.'
              : 'Banner v gridu kategorie může být buď obrázek, nebo textová karta s hodnocením zákaznice. Hodnocení automaticky obsahuje i Heureka „Ověřeno zákazníky" pečeť s počtem doporučení dole.'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {typeOptions.map((t) => {
              const selected = bannerType === t;
              const label =
                t === 'image' ? 'Obrázek' : t === 'review' ? 'Hodnocení' : 'Produkt týdne';
              const desc =
                t === 'image'
                  ? 'Klasická grafika (PNG/JPG), případně s proklikem.'
                  : t === 'review'
                    ? 'Textová karta — hvězdy + titul + recenze + autor + Heureka „Ověřeno zákazníky" s počtem doporučení.'
                    : 'Obrázek + cenový blok (původní/nová cena + sleva) + tikající odpočet do konce právě běžící akce Produkt týdne.';
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setBannerType(t)}
                  className={`text-left p-4 border-2 rounded-lg transition-all cursor-pointer ${
                    selected
                      ? 'border-primary bg-primary-50 shadow-card'
                      : 'border-border bg-surface hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        selected ? 'border-primary' : 'border-border'
                      }`}
                    >
                      {selected && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <div className="text-sm font-medium text-text">{label}</div>
                  </div>
                  <p className="text-xs text-text-muted mt-2 leading-relaxed">{desc}</p>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {placement && triggerType && !isReview && (
        <section className="mt-8">
          <SectionHeader
            title={`${typeOptions.length > 1 ? '4' : '3'}. Grafika${isProduktTydne ? ' a ceny' : ''}`}
          />
          <p className="text-sm text-text-secondary mb-4">
            Nahraj obrázek banneru zvlášť pro desktop a mobil — snippet vybere variantu podle šířky
            viewportu. Doporučené formáty: JPG, PNG, WebP. Max 3 MB / soubor.
          </p>

          <div className="space-y-5">
            <ImageUploadField
              label="Grafika pro desktop (šířka > 640 px)"
              hint={
                isCategoryProducts
                  ? 'Doporučený rozměr 754 × 1068 px (2× kvůli retina displejům — render je pak ostrý). Poměr dlaždice 377 × 534.'
                  : 'Šířka typicky 1200–1920 px, formát na šířku.'
              }
              value={desktopImage}
              onChange={setDesktopImage}
              inputId="upsell-image-desktop"
            />
            <ImageUploadField
              label="Grafika pro mobil (šířka ≤ 640 px)"
              hint={
                isCategoryProducts
                  ? 'Doporučený rozměr 640 × 640 px (čtverec). Na mobilu se banner zobrazí přes celý řádek (oba sloupce).'
                  : 'Šířka typicky 600–800 px, formát na výšku nebo čtvercový.'
              }
              value={mobileImage}
              onChange={setMobileImage}
              inputId="upsell-image-mobile"
            />
          </div>

          {isProduktTydne && (
            <div className="mt-6 border-t border-border pt-5">
              <h4 className="text-sm font-semibold text-text mb-1">Ceny a odpočet</h4>
              <p className="text-sm text-text-secondary mb-4">
                Ceny zadej jako volný text (přesně jak se mají zobrazit). Odpočet se napojí
                automaticky na konec právě běžící akce Produkt týdne — žádné datum nezadáváš.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block">
                  <span className="text-sm text-text-secondary">Původní cena (přeškrtnutá)</span>
                  <input
                    type="text"
                    value={ptPriceOriginal}
                    onChange={(e) => setPtPriceOriginal(e.target.value)}
                    placeholder="489 Kč"
                    maxLength={50}
                    className="mt-1 w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-text-secondary">Sleva</span>
                  <input
                    type="text"
                    value={ptDiscount}
                    onChange={(e) => setPtDiscount(e.target.value)}
                    placeholder="–20 %"
                    maxLength={50}
                    className="mt-1 w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-text-secondary">
                    Nová cena <span className="text-danger">*</span>
                  </span>
                  <input
                    type="text"
                    value={ptPriceNew}
                    onChange={(e) => setPtPriceNew(e.target.value)}
                    placeholder="391,20 Kč"
                    maxLength={50}
                    className="mt-1 w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary"
                  />
                </label>
              </div>
            </div>
          )}
        </section>
      )}

      {placement && triggerType && isReview && (
        <section className="mt-8">
          <SectionHeader title="4. Hodnocení" />
          <p className="text-sm text-text-secondary mb-4">
            Vyplň text hodnocení tak, jak ho má zákaznice vidět. Hvězdy, titul a autor jsou
            volitelné, samotný text recenze je povinný.
          </p>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="upsell-review-rating"
                className="block text-sm font-medium text-text mb-1.5"
              >
                Počet hvězd (0–5)
              </label>
              <input
                id="upsell-review-rating"
                type="number"
                value={reviewRating}
                onChange={(e) => setReviewRating(e.target.value)}
                min={0}
                max={5}
                step={1}
                className="w-32 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <p className="text-xs text-text-muted mt-1">0 = bez hvězd, doporučeno 5.</p>
            </div>

            <TextField
              id="upsell-review-title"
              label="Titul recenze"
              hint='Volitelné. Např. „Hodnocení od maminky".'
              value={reviewTitle}
              onChange={setReviewTitle}
              maxLength={200}
            />

            <div>
              <label
                htmlFor="upsell-review-body"
                className="block text-sm font-medium text-text mb-1.5"
              >
                Text recenze <span className="text-danger">*</span>
              </label>
              <textarea
                id="upsell-review-body"
                value={reviewBody}
                onChange={(e) => setReviewBody(e.target.value)}
                rows={6}
                maxLength={2000}
                placeholder={'Ráda v obchodě nakupuji. Velikosti jsou přesné…'}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-y"
              />
              <p className="text-xs text-text-muted mt-1">
                Můžeš psát emoji. Odřádkování zachováme.
              </p>
            </div>

            <TextField
              id="upsell-review-author"
              label="Autor"
              hint='Volitelné. Např. „Nikola Bardisová".'
              value={reviewAuthor}
              onChange={setReviewAuthor}
              maxLength={100}
            />
          </div>
        </section>
      )}

      {placement && triggerType && contentValid && (
        <section className="mt-8">
          <SectionHeader title={`${typeOptions.length > 1 ? '5' : '4'}. Detail nabídky`} />

          <div className="space-y-4">
            <TextField
              id="upsell-target-url"
              label="Cílová URL (po prokliku)"
              hint={
                isReview
                  ? 'Volitelné. Když vyplníš, celá karta hodnocení bude clickable (s mírným hover zoomem na desktopu).'
                  : 'Volitelné. Kam má klik na banner zákazníka zavést.'
              }
              type="url"
              placeholder="https://www.v-mart.cz/akce"
              value={targetUrl}
              onChange={setTargetUrl}
            />

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={targetBlank}
                onChange={(e) => setTargetBlank(e.target.checked)}
                className="w-4 h-4 accent-primary cursor-pointer"
              />
              <span className="text-sm text-text">
                Otevřít v novém okně (na nové kartě prohlížeče)
              </span>
            </label>

            {!isReview && (
              <TextField
                id="upsell-alt-text"
                label="Alternativní text"
                hint="Volitelné. Popis obrázku pro screen readery a vyhledávače."
                value={altText}
                onChange={setAltText}
                maxLength={200}
              />
            )}

            <TextField
              id="upsell-internal-name"
              label="Název nabídky (interní)"
              hint="Jen pro tebe v adminu — zákazník ho neuvidí."
              required
              value={internalName}
              onChange={setInternalName}
              maxLength={200}
            />

            <div>
              <label
                htmlFor="upsell-priority"
                className="block text-sm font-medium text-text mb-1.5"
              >
                Priorita nabídky
              </label>
              <input
                id="upsell-priority"
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                min={0}
                max={999}
                step={1}
                className="w-32 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <p className="text-xs text-text-muted mt-1">
                Vyšší číslo = banner se vykreslí dříve, pokud se na jedno místo dostane víc nabídek
                najednou. Výchozí 0.
              </p>
            </div>

            {isCategoryProducts && (
              <div>
                <label
                  htmlFor="upsell-grid-position"
                  className="block text-sm font-medium text-text mb-1.5"
                >
                  Číslo pozice v kategorii <span className="text-danger">*</span>
                </label>
                <input
                  id="upsell-grid-position"
                  type="number"
                  value={gridPosition}
                  onChange={(e) => setGridPosition(e.target.value)}
                  min={1}
                  max={100}
                  step={1}
                  placeholder="2"
                  className="w-32 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
                <p className="text-xs text-text-muted mt-1">
                  Kolikátá dlaždice v gridu kategorie (1 = úplně první). Doporučení: 2–4, ať je
                  banner viditelný „nad záhybem".
                </p>
              </div>
            )}

            <DateTimeRangeField
              startDate={startDate}
              startTime={startTime}
              endDate={endDate}
              endTime={endTime}
              onChange={(patch) => {
                if (patch.startDate !== undefined) setStartDate(patch.startDate);
                if (patch.startTime !== undefined) setStartTime(patch.startTime);
                if (patch.endDate !== undefined) setEndDate(patch.endDate);
                if (patch.endTime !== undefined) setEndTime(patch.endTime);
              }}
            />
          </div>
        </section>
      )}

      {placement && triggerType && contentValid && (
        <div className="mt-8 flex flex-col items-end gap-2">
          {!canSave && missingChecks().length > 0 && (
            <div className="text-xs text-danger text-right">
              <div className="font-medium mb-0.5">Před uložením vyplň:</div>
              <ul className="list-disc list-inside">
                {missingChecks().map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-4 py-2 text-sm text-text-secondary hover:text-text cursor-pointer disabled:opacity-50"
            >
              Zrušit
            </button>
            {isAdmin ? (
              <>
                {!isEdit && isCategoryProducts && (
                  <button
                    type="button"
                    onClick={() => handleSave({ addAnother: true })}
                    disabled={!canSave || saving || savingAndContinue}
                    className="px-5 py-2 bg-surface text-primary border border-primary text-sm font-medium rounded-lg hover:bg-primary-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      canSave
                        ? 'Uloží tento banner a vyčistí formulář pro další z téže sady (zachová spouštěč + prioritu).'
                        : `Chybí: ${missingChecks().join('; ')}`
                    }
                  >
                    {savingAndContinue ? 'Ukládám…' : 'Uložit a přidat další'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleSave()}
                  disabled={!canSave || saving || savingAndContinue}
                  className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  title={canSave ? '' : `Chybí: ${missingChecks().join('; ')}`}
                >
                  {saving ? 'Ukládám…' : isEdit ? 'Uložit změny' : 'Uložit banner'}
                </button>
              </>
            ) : (
              <div className="text-xs text-text-muted">Bannery může ukládat jen admin.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** date "YYYY-MM-DD" + time "HH:MM" → unix ms. Prázdné date = null. */
function composeDateTime(date: string, time: string, isEnd: boolean): number | null {
  if (!date) return null;
  const t = time || (isEnd ? '23:59' : '00:00');
  const d = new Date(`${date}T${t}:00`);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** unix ms → 'YYYY-MM-DD' v lokálním čase (pro <input type="date">). Null/undef → ''. */
function msToDateInput(ms: number | null | undefined): string {
  if (!ms) return '';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** unix ms → 'HH:MM' v lokálním čase (pro <input type="time">). */
function msToTimeInput(ms: number | null | undefined): string {
  if (!ms) return '';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Sestaví UploadedImage z R2 URL existující grafiky (pro edit mode pre-fill). */
function existingImageFromUrl(url: string, variant: 'desktop' | 'mobile'): UploadedImage {
  return {
    dataUrl: url, // <img src> přijme i normální URL
    fileName: `(existující ${variant === 'desktop' ? 'desktop' : 'mobil'} grafika)`,
    size: null,
    mimeType: '',
    width: null,
    height: null,
    isExisting: true,
  };
}

/** Tělo pro POST/PATCH: rozhodnout co poslat za image slot. */
function imageForApi(img: UploadedImage | null): unknown {
  if (img === null) return null; // smazat / žádný
  if (img.isExisting) return { keep: true }; // ponechat (jen PATCH)
  // nový upload
  return {
    dataUrl: img.dataUrl,
    fileName: img.fileName,
    size: img.size,
    mimeType: img.mimeType,
    width: img.width,
    height: img.height,
  };
}

// ─── Form helpers ───────────────────────────────────────────────────────

function TextField({
  id,
  label,
  hint,
  type = 'text',
  placeholder,
  required,
  value,
  onChange,
  maxLength,
}: {
  id: string;
  label: string;
  hint?: string;
  type?: 'text' | 'url';
  placeholder?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-text mb-1.5">
        {label}
        {required && <span className="text-danger ml-1">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
      />
      {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
    </div>
  );
}

function DateTimeRangeField({
  startDate,
  startTime,
  endDate,
  endTime,
  onChange,
}: {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  onChange: (patch: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  }) => void;
}) {
  return (
    <div>
      <div className="block text-sm font-medium text-text mb-2">Časové omezení nabídky</div>
      <div className="space-y-2">
        <DateTimeRow
          rowLabel="Od"
          datePlaceholder="rrrr-mm-dd"
          timePlaceholder="00:00"
          date={startDate}
          time={startTime}
          onDateChange={(v) => onChange({ startDate: v })}
          onTimeChange={(v) => onChange({ startTime: v })}
          dateId="upsell-start-date"
          timeId="upsell-start-time"
        />
        <DateTimeRow
          rowLabel="Do"
          datePlaceholder="rrrr-mm-dd"
          timePlaceholder="23:59"
          date={endDate}
          time={endTime}
          onDateChange={(v) => onChange({ endDate: v })}
          onTimeChange={(v) => onChange({ endTime: v })}
          dateId="upsell-end-date"
          timeId="upsell-end-time"
        />
      </div>
      <p className="text-xs text-text-muted mt-2">
        Volitelné. Banner se zobrazí jen v zadaném časovém okně (lokální čas). Pokud necháš prázdné,
        banner platí bez omezení.
      </p>
    </div>
  );
}

function DateTimeRow({
  rowLabel,
  datePlaceholder,
  timePlaceholder,
  date,
  time,
  onDateChange,
  onTimeChange,
  dateId,
  timeId,
}: {
  rowLabel: string;
  datePlaceholder: string;
  timePlaceholder: string;
  date: string;
  time: string;
  onDateChange: (v: string) => void;
  onTimeChange: (v: string) => void;
  dateId: string;
  timeId: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={dateId} className="text-sm text-text-secondary w-8 shrink-0 text-right pr-1">
        {rowLabel}
      </label>
      <input
        id={dateId}
        type="date"
        value={date}
        onChange={(e) => onDateChange(e.target.value)}
        placeholder={datePlaceholder}
        className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
      />
      <input
        id={timeId}
        type="time"
        value={time}
        onChange={(e) => onTimeChange(e.target.value)}
        placeholder={timePlaceholder}
        className="px-3 py-2 border border-border rounded-lg text-sm w-28 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
      />
    </div>
  );
}

// ─── Image upload field ─────────────────────────────────────────────────

function ImageUploadField({
  label,
  hint,
  value,
  onChange,
  inputId,
}: {
  label: string;
  hint?: string;
  value: UploadedImage | null;
  onChange: (next: UploadedImage | null) => void;
  inputId: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError('Nepodporovaný formát. Použij JPG, PNG, WebP, GIF nebo SVG.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setError(
        `Soubor je příliš velký (${formatBytes(file.size)}). Maximum je ${formatBytes(MAX_IMAGE_SIZE)}.`,
      );
      return;
    }
    try {
      const dataUrl = await readAsDataUrl(file);
      const dims = await readImageDimensions(dataUrl);
      onChange({
        dataUrl,
        fileName: file.name,
        size: file.size,
        mimeType: file.type,
        width: dims.width,
        height: dims.height,
      });
    } catch {
      setError('Nepodařilo se načíst soubor.');
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    // Reset, ať jde nahrát stejný název znovu po Odstranit.
    e.target.value = '';
  }

  function remove() {
    onChange(null);
    setError(null);
  }

  return (
    <div className="border border-border rounded-lg p-4 bg-surface">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <label htmlFor={inputId} className="block text-sm font-medium text-text">
            {label}
          </label>
          {hint && <p className="text-xs text-text-muted mt-0.5">{hint}</p>}
        </div>
        {value && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="px-2.5 py-1 text-xs text-text-secondary hover:text-primary border border-border rounded-md cursor-pointer"
            >
              Změnit
            </button>
            <button
              type="button"
              onClick={remove}
              className="px-2.5 py-1 text-xs text-text-secondary hover:text-danger border border-border rounded-md cursor-pointer"
            >
              Odstranit
            </button>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        onChange={onInputChange}
        className={
          value
            ? 'hidden'
            : 'block w-full text-sm text-text-secondary file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary hover:file:bg-primary/10 cursor-pointer'
        }
      />

      {error && (
        <div className="mt-2 p-2 bg-danger-light border border-danger/30 text-danger text-xs rounded-md">
          {error}
        </div>
      )}

      {value && (
        <div className="mt-2">
          <div className="border border-border rounded-md overflow-hidden bg-bg flex items-center justify-center">
            <img src={value.dataUrl} alt="Náhled" className="max-w-full max-h-72 object-contain" />
          </div>
          <div className="mt-2 text-xs text-text-muted flex items-center gap-3 flex-wrap">
            <span className="truncate max-w-[260px]">{value.fileName}</span>
            {value.size != null && <span>{formatBytes(value.size)}</span>}
            {value.width && value.height && (
              <span>
                {value.width} × {value.height} px
              </span>
            )}
            {value.isExisting && <span className="text-text-muted italic">stávající</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const res = r.result;
      if (typeof res === 'string') resolve(res);
      else reject(new Error('Soubor nejde přečíst'));
    };
    r.onerror = () => reject(r.error ?? new Error('Chyba čtení'));
    r.readAsDataURL(file);
  });
}

function readImageDimensions(
  dataUrl: string,
): Promise<{ width: number | null; height: number | null }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: null, height: null });
    img.src = dataUrl;
  });
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} kB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <h3 className="text-sm font-semibold text-primary">{title}</h3>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

// ─── Trigger sub-pickers ────────────────────────────────────────────────

function ProductsPicker({
  selected,
  onChange,
}: {
  selected: ProductLite[];
  onChange: (next: ProductLite[]) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProductLite[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ q: query.trim(), limit: '20' });
        const data = await apiFetch<{ products: ProductLite[] }>(
          `/api/propojeni/products?${params.toString()}`,
        );
        setResults(data.products);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query]);

  // Index právě taženého chipu. Při dragEnter na jiný chip rovnou prohodíme
  // pořadí (live reorder), takže uživatel vidí výslednou pozici už během tahu.
  // ref pro synchronní přístup, state jen pro vykreslení "drží se" opacity.
  const dragIdxRef = useRef<number | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  function add(p: ProductLite) {
    if (selected.some((s) => s.shopitem_id === p.shopitem_id)) return;
    onChange([...selected, p]);
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  function remove(shopitem_id: number) {
    onChange(selected.filter((s) => s.shopitem_id !== shopitem_id));
  }

  function move(from: number, to: number) {
    if (from === to || from < 0 || to < 0 || from >= selected.length || to >= selected.length) {
      return;
    }
    const next = selected.slice();
    const [item] = next.splice(from, 1);
    if (item) next.splice(to, 0, item);
    onChange(next);
  }

  return (
    <div>
      <label
        className="block text-sm font-medium text-text mb-1.5"
        htmlFor="upsell-products-search"
      >
        Vyhledej a přidej produkty
      </label>
      <div className="relative">
        <input
          id="upsell-products-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Začni psát kód nebo název (od 2 znaků)"
          className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
        {open && results.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-card-hover max-h-72 overflow-y-auto z-10">
            {results.map((p) => {
              const already = selected.some((s) => s.shopitem_id === p.shopitem_id);
              return (
                <button
                  key={p.shopitem_id}
                  type="button"
                  disabled={already}
                  onClick={() => add(p)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt=""
                      className="w-8 h-8 object-cover rounded border border-border shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-bg border border-border rounded shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-text truncate">{p.name}</div>
                    <div className="text-[11px] text-text-muted font-mono">
                      {p.parent_code ?? '—'}
                    </div>
                  </div>
                  {already && (
                    <div className="text-[11px] text-text-muted shrink-0">již vybráno</div>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted">
            hledám…
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="mt-3">
          <div className="text-xs text-text-muted mb-1.5">
            Vybráno {selected.length} {selected.length === 1 ? 'produkt' : 'produktů'}
            {selected.length > 1 && (
              <span className="ml-2 text-text-muted/70">· přetažením změníš pořadí</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {selected.map((p, idx) => {
              const isDragging = dragIdx === idx;
              return (
                <div
                  key={p.shopitem_id}
                  draggable
                  onDragStart={(e) => {
                    dragIdxRef.current = idx;
                    setDragIdx(idx);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', String(p.shopitem_id));
                  }}
                  onDragEnter={() => {
                    // Live reorder: jakmile cursor vstoupí na jiný chip,
                    // přesuneme tažený chip rovnou tam → uživatel vidí finální
                    // pozici už během tažení. dragIdxRef pak sleduje novou pozici.
                    const from = dragIdxRef.current;
                    if (from === null || from === idx) return;
                    move(from, idx);
                    dragIdxRef.current = idx;
                    setDragIdx(idx);
                  }}
                  onDragOver={(e) => {
                    // preventDefault je nutný, jinak prohlížeč drop nepovolí
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    // Reorder se už stal v dragEnter, tady jen uklidíme stav.
                    dragIdxRef.current = null;
                    setDragIdx(null);
                  }}
                  onDragEnd={() => {
                    dragIdxRef.current = null;
                    setDragIdx(null);
                  }}
                  className={`flex items-center gap-2 px-2 py-1 bg-primary-50 border border-primary/30 rounded-md text-xs text-primary cursor-grab active:cursor-grabbing select-none transition-opacity ${
                    isDragging ? 'opacity-40' : ''
                  }`}
                  title="Přetáhni pro změnu pořadí"
                >
                  <span className="text-primary/50 text-base leading-none" aria-hidden>
                    ⋮⋮
                  </span>
                  {p.image_url && (
                    <img
                      src={p.image_url}
                      alt=""
                      className="w-5 h-5 object-cover rounded pointer-events-none"
                    />
                  )}
                  <span className="max-w-[220px] truncate pointer-events-none">{p.name}</span>
                  <button
                    type="button"
                    draggable={false}
                    onDragStart={(e) => e.stopPropagation()}
                    onClick={() => remove(p.shopitem_id)}
                    className="text-primary hover:text-danger ml-1 cursor-pointer"
                    aria-label="Odebrat"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function FlagsPicker({
  selectedCodes,
  onChange,
}: {
  selectedCodes: string[];
  onChange: (next: string[]) => void;
}) {
  const [flags, setFlags] = useState<FlagRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch<{ flags: FlagRow[] }>('/api/upsell/flags');
        setFlags(data.flags);
      } catch (e: unknown) {
        setError(e instanceof ApiError ? e.message : 'Chyba načítání');
      }
    })();
  }, []);

  function toggle(code: string) {
    if (selectedCodes.includes(code)) {
      onChange(selectedCodes.filter((c) => c !== code));
    } else {
      onChange([...selectedCodes, code]);
    }
  }

  if (error) {
    return <div className="text-sm text-danger">{error}</div>;
  }
  if (!flags) {
    return <div className="text-sm text-text-muted">Načítám příznaky…</div>;
  }
  if (flags.length === 0) {
    return (
      <div className="p-3 bg-bg border border-dashed border-border rounded-lg text-sm text-text-muted">
        Příznaky zatím nejsou nasynchronizovány. Spusť sync v Nastavení (záložka „Datové zdroje ze
        Shoptetu").
      </div>
    );
  }
  return (
    <div>
      <div className="text-sm font-medium text-text mb-2">Vyber příznaky</div>
      <div className="flex flex-wrap gap-2">
        {flags.map((f) => {
          const selected = selectedCodes.includes(f.code);
          return (
            <button
              key={f.code}
              type="button"
              onClick={() => toggle(f.code)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors cursor-pointer ${
                selected
                  ? 'bg-primary text-white border-primary'
                  : 'bg-surface text-text-secondary border-border hover:border-primary/50'
              }`}
            >
              {f.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CategoryPicker({
  selectedGuids,
  onChange,
}: {
  selectedGuids: string[];
  onChange: (next: string[]) => void;
}) {
  const [categories, setCategories] = useState<CategoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch<{ categories: CategoryRow[] }>('/api/upsell/categories');
        setCategories(data.categories);
      } catch (e: unknown) {
        setError(e instanceof ApiError ? e.message : 'Chyba načítání');
      }
    })();
  }, []);

  if (error) {
    return <div className="text-sm text-danger">{error}</div>;
  }
  if (!categories) {
    return <div className="text-sm text-text-muted">Načítám kategorie…</div>;
  }
  if (categories.length === 0) {
    return (
      <div className="p-3 bg-bg border border-dashed border-border rounded-lg text-sm text-text-muted">
        Kategorie zatím nejsou nasynchronizovány. Spusť sync v Nastavení (záložka „Datové zdroje ze
        Shoptetu").
      </div>
    );
  }

  const byGuid = new Map(categories.map((c) => [c.guid, c]));

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="px-3 py-2 border border-border rounded-lg text-sm text-text bg-surface hover:border-primary/40 cursor-pointer"
      >
        Vybrat kategorie ({selectedGuids.length})
      </button>

      {selectedGuids.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selectedGuids.map((g) => {
            const c = byGuid.get(g);
            return (
              <div
                key={g}
                className="flex items-center gap-2 px-2 py-1 bg-primary-50 border border-primary/30 rounded-md text-xs text-primary"
              >
                <span className="max-w-[260px] truncate">{c?.full_name ?? c?.name ?? g}</span>
                <button
                  type="button"
                  onClick={() => onChange(selectedGuids.filter((x) => x !== g))}
                  className="text-primary hover:text-danger ml-1 cursor-pointer"
                  aria-label="Odebrat"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <CategoryTreeModal
          categories={categories}
          initialSelected={selectedGuids}
          onSave={(guids) => {
            onChange(guids);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Category tree modal ────────────────────────────────────────────────

type TreeNode = CategoryRow & { children: TreeNode[] };

function buildTree(flat: CategoryRow[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  for (const c of flat) map.set(c.guid, { ...c, children: [] });
  const roots: TreeNode[] = [];
  for (const node of map.values()) {
    if (node.parent_guid && map.has(node.parent_guid)) {
      // biome-ignore lint/style/noNonNullAssertion: existence ověřena v map.has
      map.get(node.parent_guid)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  // Setřídit děti podle sort_order, pak name.
  const sort = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'cs'));
    for (const n of nodes) sort(n.children);
  };
  sort(roots);
  return roots;
}

function CategoryTreeModal({
  categories,
  initialSelected,
  onSave,
  onClose,
}: {
  categories: CategoryRow[];
  initialSelected: string[];
  onSave: (guids: string[]) => void;
  onClose: () => void;
}) {
  const tree = buildTree(categories);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialSelected));
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    // Defaultně rozbalit větve, které obsahují právě vybrané položky
    const expand = new Set<string>();
    const byGuid = new Map(categories.map((c) => [c.guid, c]));
    for (const guid of initialSelected) {
      let cur = byGuid.get(guid);
      while (cur?.parent_guid) {
        expand.add(cur.parent_guid);
        cur = byGuid.get(cur.parent_guid);
      }
    }
    return expand;
  });
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const matches = (n: TreeNode): boolean => {
    if (!q) return true;
    if (n.name.toLowerCase().includes(q)) return true;
    if (n.full_name?.toLowerCase().includes(q)) return true;
    return n.children.some(matches);
  };

  function toggleSelect(guid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  }

  function toggleExpand(guid: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl shadow-card-hover w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text">Vybrat kategorie</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít"
            className="text-text-muted hover:text-danger text-xl leading-none cursor-pointer"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-3 border-b border-border">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Hledat v názvech kategorií…"
            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2">
          <CategoryTreeNodes
            nodes={tree}
            depth={0}
            selected={selected}
            expanded={expanded}
            onToggleSelect={toggleSelect}
            onToggleExpand={toggleExpand}
            forceExpand={!!q}
            matches={matches}
          />
        </div>

        <div className="px-6 py-3 border-t border-border flex items-center justify-between gap-3">
          <div className="text-xs text-text-muted">Vybráno: {selected.size}</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm text-text-secondary hover:text-text cursor-pointer"
            >
              Zrušit
            </button>
            <button
              type="button"
              onClick={() => onSave(Array.from(selected))}
              className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors cursor-pointer"
            >
              Uložit výběr
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryTreeNodes({
  nodes,
  depth,
  selected,
  expanded,
  onToggleSelect,
  onToggleExpand,
  forceExpand,
  matches,
}: {
  nodes: TreeNode[];
  depth: number;
  selected: Set<string>;
  expanded: Set<string>;
  onToggleSelect: (guid: string) => void;
  onToggleExpand: (guid: string) => void;
  forceExpand: boolean;
  matches: (n: TreeNode) => boolean;
}) {
  return (
    <>
      {nodes.filter(matches).map((n) => {
        const hasChildren = n.children.length > 0;
        const isExpanded = forceExpand || expanded.has(n.guid);
        const isSelected = selected.has(n.guid);
        return (
          <div key={n.guid}>
            <div
              className="flex items-center gap-1 py-1 hover:bg-surface-hover rounded"
              style={{ paddingLeft: `${depth * 16 + 4}px` }}
            >
              {hasChildren ? (
                <button
                  type="button"
                  onClick={() => onToggleExpand(n.guid)}
                  className="w-5 h-5 flex items-center justify-center text-text-muted hover:text-text cursor-pointer shrink-0"
                  aria-label={isExpanded ? 'Sbalit' : 'Rozbalit'}
                >
                  {isExpanded ? '−' : '+'}
                </button>
              ) : (
                <div className="w-5 h-5 shrink-0" />
              )}
              <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggleSelect(n.guid)}
                  className="cursor-pointer"
                />
                <span className="text-sm text-text truncate">
                  {n.name}
                  {n.full_name && n.full_name !== n.name && (
                    <span className="text-text-muted ml-2 text-xs italic">({n.full_name})</span>
                  )}
                </span>
              </label>
            </div>
            {hasChildren && isExpanded && (
              <CategoryTreeNodes
                nodes={n.children}
                depth={depth + 1}
                selected={selected}
                expanded={expanded}
                onToggleSelect={onToggleSelect}
                onToggleExpand={onToggleExpand}
                forceExpand={forceExpand}
                matches={matches}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

function PlacementSettingsView({
  settings,
  catalogStatus,
  onEdit,
  onSync,
  onShowStocks,
  onBack,
}: {
  settings: PlacementSettings;
  catalogStatus: CatalogStatus | null;
  onEdit: (p: AnyPlacement) => void;
  onSync: (kind: SyncKind) => Promise<void>;
  onShowStocks: () => void;
  onBack: () => void;
}) {
  return (
    <div className="bg-surface rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-text">Nastavení umístění</h2>
          <p className="text-sm text-text-secondary mt-0.5">
            Pro každé umístění nastav CSS selektor v šabloně e-shopu, kam snippet vloží banner /
            nabídku.
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-text-muted hover:text-text cursor-pointer"
        >
          ← Zpět na seznam
        </button>
      </div>

      <h3 className="text-sm font-semibold text-text-secondary mt-4 mb-2">Umístění bannerů</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {PLACEMENTS.map((p) => {
          const selector = settings[p.id]?.css_selector ?? null;
          return (
            <div key={p.id} className="relative border-2 border-border rounded-lg overflow-hidden">
              <div className="bg-bg aspect-[4/3] flex items-center justify-center overflow-hidden">
                <img
                  src={p.image}
                  alt={p.label}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="px-3 py-2 text-xs font-medium border-t bg-surface text-text-secondary border-border">
                <div>{p.label}</div>
                <div className="mt-1 text-[11px] font-mono truncate text-text-muted">
                  {isEarPlacement(p.id) ? (
                    <span className="italic">selektor není potřeba</span>
                  ) : selector ? (
                    selector
                  ) : (
                    <span className="italic">selektor neuložen</span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => onEdit(p.id)}
                title="Upravit CSS selektor (#id nebo .class)"
                aria-label={`Upravit selektor pro ${p.label}`}
                className="absolute top-1.5 right-1.5 w-7 h-7 flex items-center justify-center rounded-md bg-surface/95 border border-border text-text-secondary hover:text-primary hover:border-primary/40 transition-colors cursor-pointer shadow-card"
              >
                <GearIcon size={14} />
              </button>
            </div>
          );
        })}
      </div>

      <h3 className="text-sm font-semibold text-text-secondary mt-6 mb-2">Umístění nabídek</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {OFFER_PLACEMENTS.map((p) => {
          const selector = settings[p.id]?.css_selector ?? null;
          return (
            <div key={p.id} className="relative border-2 border-border rounded-lg overflow-hidden">
              <div className="bg-bg aspect-[4/3] flex items-center justify-center overflow-hidden">
                <img
                  src={p.image}
                  alt={p.label}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="px-3 py-2 text-xs font-medium border-t bg-surface text-text-secondary border-border">
                <div>{p.label}</div>
                <div className="mt-1 text-[11px] font-mono truncate text-text-muted">
                  {selector ? selector : <span className="italic">selektor neuložen</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onEdit(p.id)}
                title="Upravit CSS selektor (#id nebo .class)"
                aria-label={`Upravit selektor pro ${p.label}`}
                className="absolute top-1.5 right-1.5 w-7 h-7 flex items-center justify-center rounded-md bg-surface/95 border border-border text-text-secondary hover:text-primary hover:border-primary/40 transition-colors cursor-pointer shadow-card"
              >
                <GearIcon size={14} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-8">
        <h3 className="text-base font-semibold text-text mb-1">Datové zdroje ze Shoptetu</h3>
        <p className="text-sm text-text-secondary mb-3">
          Kategorie a příznaky se používají při výběru spouštěče banneru, skladová dostupnost pro
          cross-sell „Skladem N ks" po výběru velikosti.
        </p>
        <div className="border border-border rounded-lg overflow-hidden">
          <CatalogRow
            label="Kategorie"
            status={catalogStatus?.categories ?? null}
            onSync={() => onSync('categories')}
          />
          <div className="h-px bg-border" />
          <CatalogRow
            label="Příznaky"
            status={catalogStatus?.flags ?? null}
            onSync={() => onSync('flags')}
          />
          <div className="h-px bg-border" />
          <CatalogRow
            label="Skladová dostupnost (varianty)"
            status={catalogStatus?.stocks ?? null}
            onSync={() => onSync('stocks')}
            onShowData={onShowStocks}
          />
        </div>
      </div>
    </div>
  );
}

function CatalogRow({
  label,
  status,
  onSync,
  onShowData,
}: {
  label: string;
  status: { count: number; last_run: CatalogRun } | null;
  onSync: () => Promise<void>;
  onShowData?: () => void;
}) {
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    try {
      await onSync();
    } finally {
      setBusy(false);
    }
  }

  const count = status?.count ?? 0;
  const lastRun = status?.last_run ?? null;
  const lastStatus = lastRun?.status;
  const lastAt = lastRun?.run_at;

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-surface">
      <div>
        <div className="text-sm font-medium text-text">{label}</div>
        <div className="text-xs text-text-muted mt-0.5">
          {onShowData ? (
            <button
              type="button"
              onClick={onShowData}
              className="underline decoration-dotted hover:text-primary cursor-pointer"
            >
              {count} ks v databázi
            </button>
          ) : (
            <>{count} ks v databázi</>
          )}
          {lastAt && (
            <>
              {' · '}poslední sync {fmtDateTime(lastAt)}
              {lastStatus && lastStatus !== 'success' && (
                <span
                  className={`ml-1 text-[11px] font-semibold ${lastStatus === 'failed' ? 'text-danger' : 'text-warning'}`}
                >
                  ({lastStatus})
                </span>
              )}
            </>
          )}
          {lastRun?.error_message && (
            <div className="text-[11px] text-danger mt-0.5 truncate max-w-md">
              {lastRun.error_message}
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        className="px-3 py-1.5 text-sm font-medium rounded-md border border-border bg-surface hover:border-primary/40 hover:text-primary text-text-secondary transition-colors cursor-pointer disabled:opacity-50"
      >
        {busy ? 'Sync běží…' : 'Synchronizovat'}
      </button>
    </div>
  );
}

function GearIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function SelectorModal({
  placement,
  currentSelector,
  onSave,
  onClose,
}: {
  placement: AnyPlacement;
  currentSelector: string | null;
  onSave: (placement: AnyPlacement, selector: string | null) => Promise<void>;
  onClose: () => void;
}) {
  const [value, setValue] = useState(currentSelector ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const label = labelForAnyPlacement(placement);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = value.trim();
    if (trimmed && !/^[#.]/.test(trimmed)) {
      setError('Selektor musí začínat # (pro ID) nebo . (pro class).');
      return;
    }
    setSaving(true);
    try {
      await onSave(placement, trimmed === '' ? null : trimmed);
      onClose();
    } catch (e: unknown) {
      setError(
        e instanceof ApiError ? e.describe() : e instanceof Error ? e.message : 'Uložení selhalo',
      );
    } finally {
      setSaving(false);
    }
  }

  const isEar = isEarPlacement(placement);

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl shadow-card-hover w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-text mb-1">Nastavení umístění</h3>
        <p className="text-sm text-text-secondary mb-4">{label}</p>

        {isEar && (
          <div className="mb-3 p-3 bg-primary-50 border border-primary/30 rounded-md text-xs text-primary">
            U uší se CSS selektor nepoužívá — bannery se vždy věší na <code>body</code> přes
            <code> position: fixed</code>. Můžeš zde nic nezadávat.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-text mb-1.5" htmlFor="upsell-sel">
            CSS selektor v šabloně e-shopu
          </label>
          <input
            id="upsell-sel"
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={
              isEar ? '(volitelné — uši ho ignorují)' : '#banner-zone nebo .product-detail__gallery'
            }
            className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            ref={inputRef}
          />
          <p className="text-xs text-text-muted mt-1.5">
            Začni znakem <code className="font-mono">#</code> pro ID nebo{' '}
            <code className="font-mono">.</code> pro class. Snippet banner vloží do prvního prvku,
            který selektor najde. Prázdné = vymazat nastavení.
          </p>

          {error && (
            <div className="mt-3 p-2.5 bg-danger-light border border-danger/30 text-danger text-xs rounded-md">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-2 text-sm text-text-secondary hover:text-text cursor-pointer disabled:opacity-50"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Ukládám…' : 'Uložit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function labelFor(p: BannerPlacement): string {
  return PLACEMENTS.find((x) => x.id === p)?.label ?? p;
}

// ─── Offer wizard ───────────────────────────────────────────────────────

function OfferWizard({
  isAdmin,
  existingOffer,
  onCancel,
  onSaved,
  onError,
}: {
  isAdmin: boolean;
  existingOffer: OfferDto | null;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
  onError: (msg: string) => void;
}) {
  const isEdit = existingOffer != null;

  // Step 1: Placement
  const [placement, setPlacement] = useState<OfferPlacement | null>(
    () => existingOffer?.placement ?? null,
  );
  // Step 2: Trigger
  const [triggerType, setTriggerType] = useState<TriggerType | null>(
    () => existingOffer?.trigger_type ?? null,
  );
  const [selectedTriggerProducts, setSelectedTriggerProducts] = useState<ProductLite[]>([]);
  const [selectedCategoryGuids, setSelectedCategoryGuids] = useState<string[]>(
    () => existingOffer?.trigger_category_guids ?? [],
  );
  const [selectedFlagCodes, setSelectedFlagCodes] = useState<string[]>(
    () => existingOffer?.trigger_flag_codes ?? [],
  );

  // Step 3: Doporučené produkty
  const [recommendedProducts, setRecommendedProducts] = useState<ProductLite[]>([]);
  // Výjimky — produkty, na kterých se zaškrtávátko nezobrazí.
  const [excludedProducts, setExcludedProducts] = useState<ProductLite[]>([]);

  // Step 4: Detail — internal_name a priority byly zrušeny v UI; internal_name
  // se auto-generuje při save z trigger kontextu, priority je vždy 0.
  const [sectionTitle, setSectionTitle] = useState(() => existingOffer?.section_title ?? '');
  // Období jen u zaškrtávátka (sezónní doplněk); ostatní nabídky platí napořád.
  const [od, setOd] = useState(() => msToDen(existingOffer?.start_at));
  const [doDen, setDoDen] = useState(() => msToDen(existingOffer?.end_at));
  const [saving, setSaving] = useState(false);
  const jeCheckbox = placement === 'offer-checkbox';
  const maxProduktu = jeCheckbox ? 1 : 6;

  // Reset trigger pokud uživatel přepne placement na cart (kde nejsou produkty/flag triggery povolené)
  useEffect(() => {
    if (!placement || !triggerType) return;
    const allowed = allowedTriggersForOffer(placement);
    if (!allowed.includes(triggerType)) {
      setTriggerType(null);
      setSelectedTriggerProducts([]);
      setSelectedFlagCodes([]);
    }
  }, [placement, triggerType]);

  // Pre-fill produktů v edit módu (potřebujeme detaily přes /products?ids=…)
  useEffect(() => {
    if (!existingOffer) return;
    const triggerIds = existingOffer.trigger_product_ids;
    const recIds = existingOffer.product_ids;
    const excludeIds = existingOffer.exclude_product_ids ?? [];
    const all = Array.from(new Set([...triggerIds, ...recIds, ...excludeIds]));
    if (all.length === 0) return;
    (async () => {
      try {
        const params = new URLSearchParams({ ids: all.join(',') });
        const data = await apiFetch<{ products: ProductLite[] }>(
          `/api/propojeni/products?${params.toString()}`,
        );
        const byId = new Map(data.products.map((p) => [p.shopitem_id, p]));
        setSelectedTriggerProducts(
          triggerIds.map((id) => byId.get(id)).filter((p): p is ProductLite => p !== undefined),
        );
        setRecommendedProducts(
          recIds.map((id) => byId.get(id)).filter((p): p is ProductLite => p !== undefined),
        );
        setExcludedProducts(
          excludeIds.map((id) => byId.get(id)).filter((p): p is ProductLite => p !== undefined),
        );
      } catch {
        /* edge case — neznámé produkty se prostě nevyrenderují */
      }
    })();
  }, [existingOffer]);

  const canSave =
    !!placement &&
    !!triggerType &&
    recommendedProducts.length > 0 &&
    recommendedProducts.length <= maxProduktu &&
    !(jeCheckbox && od && doDen && doDen < od) &&
    (triggerType !== 'products' || selectedTriggerProducts.length > 0) &&
    (triggerType !== 'category' || selectedCategoryGuids.length > 0) &&
    (triggerType !== 'flag' || selectedFlagCodes.length > 0);

  async function handleSave() {
    if (!canSave || !placement || !triggerType) return;
    setSaving(true);
    try {
      // Auto-generated interní název z trigger kontextu — uživatel ho nezadává.
      // Pro existing offer zachováváme původní (kdyby měl smysluplný custom název).
      const autoName =
        existingOffer?.internal_name ||
        buildOfferInternalName(
          triggerType,
          selectedTriggerProducts,
          selectedCategoryGuids,
          selectedFlagCodes,
          placement,
        );
      const body = {
        internal_name: autoName,
        placement,
        trigger_type: triggerType,
        trigger_product_ids: selectedTriggerProducts.map((p) => p.shopitem_id),
        trigger_category_guids: selectedCategoryGuids,
        trigger_flag_codes: selectedFlagCodes,
        product_ids: recommendedProducts.map((p) => p.shopitem_id),
        exclude_product_ids: jeCheckbox ? excludedProducts.map((p) => p.shopitem_id) : [],
        section_title: sectionTitle.trim() || null,
        priority: 0,
        // Nabídky platí napořád — uživatel chtěl odstranit timing UI.
        // Výjimka: zaškrtávátko (sezónní doplněk) má období od–do.
        start_at: jeCheckbox ? denToMs(od, false) : null,
        end_at: jeCheckbox ? denToMs(doDen, true) : null,
        active: existingOffer?.active ?? true,
      };
      if (isEdit && existingOffer) {
        await apiFetch(`/api/upsell/offers/${existingOffer.id}`, {
          method: 'PATCH',
          body,
        });
      } else {
        await apiFetch('/api/upsell/offers', { method: 'POST', body });
      }
      await onSaved();
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-surface rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-text">
          {isEdit ? `Upravit nabídku — ${existingOffer?.internal_name}` : 'Nová nabídka'}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-text-muted hover:text-text cursor-pointer"
        >
          Zrušit
        </button>
      </div>
      <p className="text-sm text-text-secondary mb-6">
        Cross-sell — doporučené produkty se zobrazí na detailu produktu nebo v košíku.
      </p>

      {/* Step 1 — Umístění */}
      <section>
        <SectionHeader title="1. Umístění" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {OFFER_PLACEMENTS.map((p) => {
            const selected = placement === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPlacement(p.id)}
                className={`text-left border-2 rounded-lg overflow-hidden transition-all cursor-pointer ${
                  selected ? 'border-primary shadow-card' : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="bg-bg aspect-[4/3] flex items-center justify-center overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.label}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div
                  className={`px-3 py-2 text-xs font-medium border-t ${
                    selected
                      ? 'bg-primary-50 text-primary border-primary/30'
                      : 'bg-surface text-text-secondary border-border'
                  }`}
                >
                  <div>{p.label}</div>
                  <div className="mt-1 text-[11px] text-text-muted">{p.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Step 2 — Spouštěč */}
      {placement && (
        <section className="mt-8">
          <SectionHeader title="2. Spouštěč" />
          <p className="text-sm text-text-secondary mb-3">
            Vyber, kdy se má nabídka zobrazit.
            {placement === 'offer-cart' && (
              <span className="text-text-muted">
                {' '}
                V košíku není kontext konkrétního produktu — povoleny jen Kategorie a Všude.
              </span>
            )}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {TRIGGER_TYPES.filter((t) => allowedTriggersForOffer(placement).includes(t.id)).map(
              (t) => {
                const selected = triggerType === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTriggerType(t.id)}
                    className={`text-left p-4 border-2 rounded-lg transition-all cursor-pointer ${
                      selected
                        ? 'border-primary bg-primary-50 shadow-card'
                        : 'border-border bg-surface hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          selected ? 'border-primary' : 'border-border'
                        }`}
                      >
                        {selected && <div className="w-2 h-2 rounded-full bg-primary" />}
                      </div>
                      <div className="text-sm font-medium text-text">{t.label}</div>
                    </div>
                    <p className="text-xs text-text-muted mt-2 leading-relaxed">{t.description}</p>
                  </button>
                );
              },
            )}
          </div>

          {triggerType && (
            <div className="mt-4">
              {triggerType === 'products' && (
                <ProductsPicker
                  selected={selectedTriggerProducts}
                  onChange={setSelectedTriggerProducts}
                />
              )}
              {triggerType === 'category' && (
                <CategoryPicker
                  selectedGuids={selectedCategoryGuids}
                  onChange={setSelectedCategoryGuids}
                />
              )}
              {triggerType === 'flag' && (
                <FlagsPicker selectedCodes={selectedFlagCodes} onChange={setSelectedFlagCodes} />
              )}
              {triggerType === 'everywhere' && (
                <div className="p-3 bg-bg border border-border rounded-lg text-sm text-text-secondary">
                  Nabídka se zobrazí všude, kde dané umístění existuje.
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Step 3 — Doporučené produkty */}
      {placement && triggerType && (
        <section className="mt-8">
          <SectionHeader title={jeCheckbox ? '3. Doplněk' : '3. Doporučené produkty'} />
          <p className="text-sm text-text-secondary mb-3">
            {jeCheckbox
              ? 'Vyber jeden produkt bez variant (např. Vánoční balení), který si zákazník může zaškrtnout.'
              : 'Vyber 1–6 produktů, které se zákazníkovi zobrazí jako doporučení (v pořadí, ve kterém je přidáš).'}
          </p>
          <ProductsPicker selected={recommendedProducts} onChange={setRecommendedProducts} />
          {recommendedProducts.length > maxProduktu && (
            <div className="mt-2 text-xs text-danger">
              {jeCheckbox
                ? 'Zaškrtávátko nabízí jen jeden produkt — odeber ostatní.'
                : 'Maximum 6 produktů — odeber nějaké před uložením.'}
            </div>
          )}
        </section>
      )}

      {/* Step 4 — Nadpis sekce (jediné zbylé pole; interní název a priorita
          se uživateli neukazují — auto-generujeme při save). */}
      {placement && triggerType && recommendedProducts.length > 0 && (
        <section className="mt-8">
          <SectionHeader title="4. Detail nabídky" />
          <div className="space-y-4">
            <TextField
              id="offer-section-title"
              label={jeCheckbox ? 'Text u zaškrtávátka (volitelné)' : 'Nadpis sekce (volitelné)'}
              hint={
                jeCheckbox
                  ? 'Např. „Přidat vánoční balení". Prázdné = název produktu. Cena se doplní sama.'
                  : 'Zobrazí se nad doporučenými produkty, např. „Související produkty".'
              }
              value={sectionTitle}
              onChange={setSectionTitle}
              maxLength={200}
            />
            {jeCheckbox && <OfferObdobi od={od} do={doDen} onOd={setOd} onDo={setDoDen} />}
            {jeCheckbox && (
              <div>
                <div className="text-sm font-medium text-text mb-1">
                  Nezobrazovat u produktů (volitelné)
                </div>
                <p className="text-xs text-text-muted mb-2">
                  Na detailu těchto produktů se zaškrtávátko neukáže, i když jinak splňují spouštěč.
                </p>
                <ProductsPicker selected={excludedProducts} onChange={setExcludedProducts} />
              </div>
            )}
          </div>
        </section>
      )}

      {placement && triggerType && recommendedProducts.length > 0 && (
        <div className="mt-8 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text cursor-pointer disabled:opacity-50"
          >
            Zrušit
          </button>
          {isAdmin ? (
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave || saving}
              className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Ukládám…' : isEdit ? 'Uložit změny' : 'Uložit nabídku'}
            </button>
          ) : (
            <div className="text-xs text-text-muted">Nabídky může ukládat jen admin.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Variant stocks browser modal ────────────────────────────────────────

type VariantStockRow = {
  variant_code: string;
  quantity: number;
  availability_text: string | null;
  updated_at: number;
};

/**
 * Rychlé vytvoření další kombinace z existující nabídky.
 *
 * Princip: jedna „sada" produktů (např. {K1056, CE1001, T1089}) může mít
 * víc kombinací — každá s jiným produktem jako trigger („kotvou"). Když
 * je zákazník na detailu daného produktu, vidí kombinaci, kde je tento
 * produkt kotva, a ostatní jako doporučené.
 *
 * Modal nechá uživatele jen zvolit, který z dosavadních doporučených
 * produktů bude novou kotvou, upravit pořadí ostatních a uložit.
 * Placement, prioritu i section_title přebíráme ze zdrojové nabídky.
 */
function QuickCombinationModal({
  sourceOffer,
  existingAnchorIds,
  onClose,
  onCreated,
  onError,
}: {
  sourceOffer: OfferDto;
  // shopitem_ids produktů, které jsou už kotvou v některé z kombinací stejné sady.
  // Tyto z výběru vyfiltrujeme — duplicitní kombinace nedává smysl.
  existingAnchorIds: number[];
  onClose: () => void;
  onCreated: () => void;
  onError: (msg: string) => void;
}) {
  // Dosavadní kotva + všichni doporučení tvoří sadu.
  const sourceAnchor = sourceOffer.trigger_product;
  // VŠECHNY doporučené produkty zdroje — používáme pro vyplnění doporučených
  // produktů nové kombinace (sada minus nová kotva). Tady NEFILTRUJEME.
  const allRecProducts: ProductLite[] = sourceOffer.recommended_products.map((p) => ({
    shopitem_id: p.shopitem_id,
    name: p.name,
    parent_code: p.parent_code,
    image_url: p.image_url,
  }));
  // Kandidáti pro NOVOU KOTVU = jen ti, kteří ještě nejsou kotvou v žádné
  // kombinaci stejné sady. Brání duplicitě.
  const existingSet = new Set(existingAnchorIds);
  const anchorCandidates: ProductLite[] = allRecProducts.filter(
    (p) => !existingSet.has(p.shopitem_id),
  );

  // Defaultně 1. dostupný produkt jako nová kotva.
  const firstCandidateId = anchorCandidates[0]?.shopitem_id ?? 0;
  const [newAnchorId, setNewAnchorId] = useState<number>(firstCandidateId);
  // buildRecOrder dostává FULL set (allRecProducts), aby v doporučených byly
  // i produkty, které jsou kotvou jiné kombinace (= patří do sady).
  const [recOrder, setRecOrder] = useState<ProductLite[]>(() =>
    buildRecOrder(sourceAnchor, allRecProducts, firstCandidateId),
  );
  const [internalName, setInternalName] = useState<string>(() =>
    buildName(anchorCandidates.find((c) => c.shopitem_id === firstCandidateId) ?? null),
  );
  const [submitting, setSubmitting] = useState(false);
  const placementLabel =
    OFFER_PLACEMENTS.find((p) => p.id === sourceOffer.placement)?.label ?? sourceOffer.placement;

  function changeAnchor(id: number) {
    setNewAnchorId(id);
    // Pro doporučené opět používáme FULL set (sada minus nová kotva).
    setRecOrder(buildRecOrder(sourceAnchor, allRecProducts, id));
    const a = anchorCandidates.find((c) => c.shopitem_id === id);
    if (a) setInternalName(buildName(a));
  }

  async function submit() {
    if (!internalName.trim()) {
      onError('Vyplň interní název');
      return;
    }
    if (recOrder.length === 0) {
      onError('Doporučené produkty nesmí být prázdné');
      return;
    }
    setSubmitting(true);
    try {
      await apiFetch('/api/upsell/offers', {
        method: 'POST',
        body: {
          internal_name: internalName.trim(),
          placement: sourceOffer.placement,
          trigger_type: 'products',
          trigger_product_ids: [newAnchorId],
          trigger_category_guids: [],
          trigger_flag_codes: [],
          product_ids: recOrder.map((r) => r.shopitem_id),
          section_title: sourceOffer.section_title,
          priority: sourceOffer.priority,
          start_at: null,
          end_at: null,
          active: true,
        },
      });
      onCreated();
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Vytvoření kombinace selhalo');
    } finally {
      setSubmitting(false);
    }
  }

  if (!sourceAnchor || anchorCandidates.length === 0) {
    // Rozlišíme dvě situace: chybí trigger/rec produkty (nemělo by nastat
    // v běžném flow), vs. všechny rec produkty už jsou kotvou jiné kombinace
    // (sada je kompletní → nemá co přidávat).
    const noCandidatesLeft = sourceAnchor !== null && sourceOffer.recommended_products.length > 0;
    return (
      <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
        <div className="bg-surface rounded-xl shadow-card-hover w-full max-w-md p-6">
          <div className="text-sm text-text">
            {noCandidatesLeft
              ? 'Všechny produkty z této sady už mají svou kombinaci — sada je kompletní.'
              : 'Nelze vytvořit kombinaci — zdrojová nabídka musí mít trigger produkt i alespoň 1 doporučený produkt.'}
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm bg-bg border border-border rounded-md hover:bg-surface cursor-pointer"
            >
              Zavřít
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl shadow-card-hover w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-text">+ Další kombinace</h3>
            <p className="text-xs text-text-muted mt-0.5">
              Umístění: {placementLabel} (stejné jako zdroj)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text text-xl leading-none cursor-pointer"
            aria-label="Zavřít"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 flex flex-col gap-5">
          {/* Volba nové kotvy */}
          <div>
            <div className="text-sm font-medium text-text mb-2">
              Vyber novou kotvu (trigger produkt)
            </div>
            <div className="flex flex-col gap-1.5">
              {anchorCandidates.map((p) => {
                const checked = newAnchorId === p.shopitem_id;
                return (
                  <label
                    key={p.shopitem_id}
                    className={`flex items-center gap-3 p-2 border rounded-md cursor-pointer transition-colors ${
                      checked
                        ? 'border-primary bg-primary-50'
                        : 'border-border hover:border-primary/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="new-anchor"
                      value={p.shopitem_id}
                      checked={checked}
                      onChange={() => changeAnchor(p.shopitem_id)}
                      className="accent-primary"
                    />
                    <div className="w-10 h-10 bg-bg border border-border rounded flex items-center justify-center overflow-hidden shrink-0">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt=""
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <span className="text-[9px] text-text-muted">—</span>
                      )}
                    </div>
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="text-sm font-mono text-text">{p.parent_code ?? '—'}</div>
                      <div className="text-xs text-text-muted truncate">{p.name}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Pořadí doporučených (auto-vyplněné, drag-drop) */}
          <div>
            <div className="text-sm font-medium text-text mb-2">
              Doporučené produkty (přetáhni pro změnu pořadí)
            </div>
            <ProductsPicker selected={recOrder} onChange={setRecOrder} />
          </div>

          {/* Interní název se neukazuje — auto-generuje se podle nové kotvy. */}
        </div>

        <div className="px-6 py-3 border-t border-border flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-sm bg-bg border border-border rounded-md hover:bg-surface cursor-pointer disabled:opacity-50"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="px-4 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary-hover cursor-pointer disabled:opacity-50"
          >
            {submitting ? 'Vytvářím…' : 'Vytvořit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helpery pro QuickCombinationModal
function buildRecOrder(
  sourceAnchor: ProductLite | null,
  candidates: ProductLite[],
  newAnchorId: number,
): ProductLite[] {
  if (!sourceAnchor) return [];
  // Sada = původní kotva + všichni doporučení; pak odebereme nově vybranou kotvu.
  const all = [sourceAnchor, ...candidates];
  return all.filter((p) => p.shopitem_id !== newAnchorId);
}

function buildName(anchor: ProductLite | null): string {
  if (!anchor) return '';
  const code = anchor.parent_code ?? String(anchor.shopitem_id);
  return `${code} - ${anchor.name}`;
}

function VariantStocksModal({ onClose }: { onClose: () => void }) {
  const PAGE_SIZE = 100;
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<VariantStockRow[]>([]);
  const [total, setTotal] = useState(0);
  const [matched, setMatched] = useState(0);
  const [loading, setLoading] = useState(false);

  // Debounce vyhledávání
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setOffset(0);
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          offset: String(offset),
        });
        if (debouncedQuery) params.set('q', debouncedQuery);
        const data = await apiFetch<{
          stocks: VariantStockRow[];
          total: number;
          matched: number;
        }>(`/api/upsell/variant-stocks?${params.toString()}`);
        setRows(data.stocks);
        setTotal(data.total);
        setMatched(data.matched);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [debouncedQuery, offset]);

  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < matched;

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl shadow-card-hover w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-text">Skladová dostupnost — náhled dat</h3>
            <p className="text-xs text-text-muted mt-0.5">
              {matched.toLocaleString('cs')} z celkem {total.toLocaleString('cs')} variant
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít"
            className="text-text-muted hover:text-danger text-xl leading-none cursor-pointer"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-3 border-b border-border">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Hledat podle SKU (např. K1056)"
            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg sticky top-0">
              <tr className="text-left text-text-muted text-xs uppercase tracking-wider">
                <th className="px-6 py-2 font-semibold">Variantní SKU</th>
                <th className="px-6 py-2 font-semibold">Dostupnost</th>
                <th className="px-6 py-2 font-semibold text-right">Ks</th>
                <th className="px-6 py-2 font-semibold text-right">Aktualizováno</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-6 text-center text-text-muted text-sm">
                    Načítám…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-6 text-center text-text-muted text-sm">
                    Žádné varianty pro tento dotaz.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const availLower = (r.availability_text ?? '').toLowerCase();
                  const isInStock = availLower.startsWith('skladem');
                  const isSoldOut = availLower === 'vyprodáno';
                  return (
                    <tr key={r.variant_code} className="border-t border-border">
                      <td className="px-6 py-2 font-mono text-text">{r.variant_code}</td>
                      <td
                        className={`px-6 py-2 ${
                          isInStock
                            ? 'text-success font-medium'
                            : isSoldOut
                              ? 'text-danger'
                              : 'text-text'
                        }`}
                      >
                        {r.availability_text ?? '—'}
                      </td>
                      <td
                        className={`px-6 py-2 text-right ${r.quantity > 0 ? 'text-text-secondary' : 'text-text-muted'}`}
                      >
                        {r.quantity}
                      </td>
                      <td className="px-6 py-2 text-right text-text-muted text-xs">
                        {fmtDateTime(r.updated_at)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-3 border-t border-border flex items-center justify-between gap-3">
          <div className="text-xs text-text-muted">
            Zobrazeno {offset + 1}–{Math.min(offset + PAGE_SIZE, matched)} z{' '}
            {matched.toLocaleString('cs')}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={!hasPrev}
              className="px-3 py-1.5 text-xs border border-border rounded-md text-text-secondary hover:text-primary hover:border-primary/40 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Předchozí
            </button>
            <button
              type="button"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={!hasNext}
              className="px-3 py-1.5 text-xs border border-border rounded-md text-text-secondary hover:text-primary hover:border-primary/40 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Další →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Statistika nabídky — modal s date range, totals, line chart, per-prod table ──

type StatsSeriesPoint = {
  date: string;
  impressions: number;
  clicks: number;
  cart_clicks: number;
  clicks_value: number;
  cart_clicks_value: number;
};

type StatsTotals = {
  impressions: number;
  clicks: number;
  cart_clicks: number;
  clicks_value: number;
  cart_clicks_value: number;
};

type StatsPerProduct = {
  shopitem_id: number;
  name: string;
  image_url: string | null;
  parent_code: string | null;
  product_clicks: number;
  cart_clicks: number;
  product_clicks_value: number;
  cart_clicks_value: number;
};

type OfferStatsResponse = {
  from: string;
  to: string;
  totals: StatsTotals;
  series: StatsSeriesPoint[];
  per_product: StatsPerProduct[];
};

type StatsMetric = 'clicks' | 'clicks_value' | 'cart_clicks' | 'cart_clicks_value' | 'impressions';

const METRIC_LABELS: Record<StatsMetric, string> = {
  impressions: 'Zobrazení',
  clicks: 'Proklik (počet)',
  clicks_value: 'Proklik (Kč)',
  cart_clicks: 'Do košíku (počet)',
  cart_clicks_value: 'Do košíku (Kč)',
};

function dateInputToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function dateInputDaysAgo(days: number): string {
  const d = new Date(Date.now() - days * 86_400_000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatCzk(n: number): string {
  return `${Math.round(n).toLocaleString('cs-CZ')} Kč`;
}

function formatCount(n: number): string {
  return n.toLocaleString('cs-CZ');
}

// Datum, od kterého widget posílá efektivní cenu varianty v track POST.
// Před tímto datem se kliky/košíky ukládaly bez hodnoty (Kč = 0 by design).
const VALUE_TRACKING_SINCE = '2026-06-06';

function OfferStatsModal({ onClose }: { onClose: () => void }) {
  const [from, setFrom] = useState(() => dateInputDaysAgo(29));
  const [to, setTo] = useState(() => dateInputToday());
  const [data, setData] = useState<OfferStatsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState<StatsMetric>('cart_clicks_value');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    apiFetch<OfferStatsResponse>(`/api/upsell/offers/stats?from=${from}&to=${to}`)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.describe() : 'Načtení selhalo');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  function applyPreset(days: number) {
    setFrom(dateInputDaysAgo(days - 1));
    setTo(dateInputToday());
  }

  const series = data?.series ?? [];
  const totals = data?.totals ?? {
    impressions: 0,
    clicks: 0,
    cart_clicks: 0,
    clicks_value: 0,
    cart_clicks_value: 0,
  };
  const perProduct = data?.per_product ?? [];

  const ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : null;
  const cartRate = totals.impressions > 0 ? (totals.cart_clicks / totals.impressions) * 100 : null;

  const isMoney = metric === 'clicks_value' || metric === 'cart_clicks_value';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-xl border border-border shadow-card max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-text">Statistika nabídek</h2>
            <p className="text-sm text-text-secondary mt-0.5">
              Souhrn napříč všemi nabídkami za zvolené období.
            </p>
            <p className="text-xs text-text-muted mt-1">
              Hodnota (Kč) se sleduje od {VALUE_TRACKING_SINCE.split('-').reverse().join('. ')}.
              Starší kliky se počítají jen do počtu.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text text-2xl leading-none cursor-pointer px-2"
            aria-label="Zavřít"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Date range + presets */}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-text-muted mb-1">Od</label>
              <input
                type="date"
                value={from}
                max={to}
                onChange={(e) => setFrom(e.target.value)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Do</label>
              <input
                type="date"
                value={to}
                min={from}
                max={dateInputToday()}
                onChange={(e) => setTo(e.target.value)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:border-primary"
              />
            </div>
            <div className="flex gap-1">
              {([7, 30, 90] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => applyPreset(d)}
                  className="text-xs px-2.5 py-1.5 border border-border rounded-md text-text-secondary hover:text-primary hover:border-primary/40 cursor-pointer"
                >
                  {d} dní
                </button>
              ))}
            </div>
          </div>

          {err && (
            <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-3 py-2">
              {err}
            </div>
          )}

          {/* Totals tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatsTile label="Zobrazení" value={formatCount(totals.impressions)} />
            <StatsTile
              label="Proklik"
              value={formatCount(totals.clicks)}
              sub={ctr !== null ? `CTR ${ctr.toFixed(1)} %` : null}
            />
            <StatsTile label="Proklik hodnota" value={formatCzk(totals.clicks_value)} />
            <StatsTile
              label="Do košíku"
              value={formatCount(totals.cart_clicks)}
              sub={cartRate !== null ? `Konv. ${cartRate.toFixed(1)} %` : null}
              accent
            />
            <StatsTile
              label="Do košíku hodnota"
              value={formatCzk(totals.cart_clicks_value)}
              accent
            />
          </div>

          {/* Metric switcher + chart */}
          <div>
            <div className="flex flex-wrap gap-1 mb-3">
              {(Object.keys(METRIC_LABELS) as StatsMetric[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMetric(m)}
                  className={`text-xs px-3 py-1.5 rounded-md border cursor-pointer ${
                    metric === m
                      ? 'bg-primary text-white border-primary'
                      : 'bg-surface text-text-secondary border-border hover:border-primary/40'
                  }`}
                >
                  {METRIC_LABELS[m]}
                </button>
              ))}
            </div>
            {loading && !data ? (
              <div className="py-12 text-center text-text-muted text-sm">Načítám…</div>
            ) : (
              <StatsLineChart
                series={series.map((p) => ({ date: p.date, value: p[metric] }))}
                isMoney={isMoney}
              />
            )}
          </div>

          {/* Per-product breakdown */}
          {perProduct.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-text mb-2">
                Souhrn produktů — sečteno napříč všemi nabídkami
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-text-muted border-b border-border">
                      <th className="text-left font-medium py-2 pr-3">Produkt</th>
                      <th className="text-right font-medium py-2 px-2">Proklik</th>
                      <th className="text-right font-medium py-2 px-2">Proklik Kč</th>
                      <th className="text-right font-medium py-2 px-2">Košík</th>
                      <th className="text-right font-medium py-2 pl-2">Košík Kč</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perProduct.map((p) => (
                      <tr key={p.shopitem_id} className="border-b border-border/50 last:border-0">
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            {p.image_url ? (
                              <img
                                src={p.image_url}
                                alt=""
                                className="w-8 h-8 object-contain bg-bg border border-border rounded"
                              />
                            ) : (
                              <div className="w-8 h-8 bg-bg border border-border rounded" />
                            )}
                            <div className="min-w-0">
                              <div className="text-xs font-mono text-text-muted">
                                {p.parent_code ?? '—'}
                              </div>
                              <div className="text-xs text-text truncate" title={p.name}>
                                {p.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="text-right py-2 px-2 tabular-nums">
                          {formatCount(p.product_clicks)}
                        </td>
                        <td className="text-right py-2 px-2 tabular-nums text-text-muted">
                          {formatCzk(p.product_clicks_value)}
                        </td>
                        <td className="text-right py-2 px-2 tabular-nums font-medium">
                          {formatCount(p.cart_clicks)}
                        </td>
                        <td className="text-right py-2 pl-2 tabular-nums text-primary font-medium">
                          {formatCzk(p.cart_clicks_value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatsTile({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string | null;
  accent?: boolean;
}) {
  return (
    <div
      className={`border rounded-lg p-3 ${
        accent ? 'bg-primary-50 border-primary/30' : 'bg-bg border-border'
      }`}
    >
      <div className="text-[11px] text-text-muted uppercase tracking-wide">{label}</div>
      <div
        className={`text-lg font-bold mt-1 tabular-nums ${accent ? 'text-primary' : 'text-text'}`}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] text-text-muted mt-0.5">{sub}</div>}
    </div>
  );
}

// Jednoduchý SVG line chart se zero-fill řadou — bez externí závislosti.
function StatsLineChart({
  series,
  isMoney,
}: {
  series: Array<{ date: string; value: number }>;
  isMoney: boolean;
}) {
  const W = 760;
  const H = 240;
  const PAD = { l: 56, r: 16, t: 16, b: 28 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  if (series.length === 0) {
    return <div className="text-sm text-text-muted py-12 text-center">Žádná data v okně.</div>;
  }

  const max = Math.max(...series.map((p) => p.value), 1);
  const stepX = series.length > 1 ? innerW / (series.length - 1) : innerW;

  function x(i: number) {
    return PAD.l + i * stepX;
  }
  function y(v: number) {
    return PAD.t + innerH - (v / max) * innerH;
  }
  function fmt(v: number) {
    return isMoney ? formatCzk(v) : formatCount(v);
  }

  const linePath = series
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L ${x(series.length - 1).toFixed(1)} ${y(0).toFixed(1)} L ${x(0).toFixed(1)} ${y(0).toFixed(1)} Z`;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * max);
  const labelEvery = Math.max(1, Math.floor(series.length / 7));

  function handleMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const pxX = e.clientX - rect.left;
    const svgX = (pxX / rect.width) * W;
    const idx = Math.round((svgX - PAD.l) / (stepX || 1));
    if (idx < 0 || idx >= series.length) {
      setHover(null);
      return;
    }
    setHover(idx);
  }

  return (
    <div className="border border-border rounded-lg bg-bg p-3 relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto block cursor-crosshair"
        preserveAspectRatio="xMidYMid meet"
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t, i) => {
          const yy = y(t);
          return (
            <g key={i}>
              <line
                x1={PAD.l}
                x2={W - PAD.r}
                y1={yy}
                y2={yy}
                stroke="currentColor"
                strokeOpacity="0.08"
              />
              <text
                x={PAD.l - 6}
                y={yy + 3}
                textAnchor="end"
                className="text-[10px] fill-current text-text-muted"
              >
                {fmt(t)}
              </text>
            </g>
          );
        })}
        <path d={areaPath} fill="#ec1268" fillOpacity="0.08" />
        <path d={linePath} stroke="#ec1268" strokeWidth="2" fill="none" />
        {series.map((p, i) =>
          i % labelEvery === 0 ? (
            <text
              key={p.date}
              x={x(i)}
              y={H - PAD.b + 16}
              textAnchor="middle"
              className="text-[10px] fill-current text-text-muted"
            >
              {p.date.slice(5)}
            </text>
          ) : null,
        )}
        {hover !== null && series[hover] && (
          <g pointerEvents="none">
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={PAD.t}
              y2={H - PAD.b}
              stroke="#ec1268"
              strokeOpacity="0.4"
              strokeDasharray="3 3"
            />
            <circle cx={x(hover)} cy={y(series[hover].value)} r={4} fill="#ec1268" />
          </g>
        )}
      </svg>
      {hover !== null && series[hover] && (
        <div className="absolute top-2 right-2 bg-surface border border-border rounded-md px-3 py-2 text-xs shadow-card">
          <div className="text-text-muted">{series[hover].date}</div>
          <div className="font-bold text-primary mt-0.5">{fmt(series[hover].value)}</div>
        </div>
      )}
    </div>
  );
}
